/**
 * Magic Park Times - Application principale
 * 
 * Gestion du thème clair/sombre :
 * 1. Priorité au localStorage (préférence utilisateur sauvegardée)
 * 2. Si aucune préférence n'est sauvegardée : mode sombre par défaut
 * 3. La préférence système n'écrase pas ce fallback
 */

import React, { useState, useEffect, lazy, Suspense, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { WeatherProvider } from './contexts/WeatherContext';
import { ModalProvider } from './contexts/ModalContext';
import { initLazyLoading } from './utils/useInView';
import { isTesla, isMobile, isIOS as isIOSDevice } from './utils/deviceDetection';
import { onAuthEvent, AUTH_EVENTS } from './utils/userPreferencesCookie';
import TeslaSplashScreen from './components/TeslaSplashScreen';
import GeneralSplashScreen from './components/GeneralSplashScreen';
import HeaderLogo from './components/HeaderLogo';
import BatteryBanner from './components/BatteryBanner';
import SEOManager from './components/SEOManager';

// Base styles (à charger en premier)
import './styles/base/variables.css';
import './styles/base/reset.css';
import './styles/base/global.css';

// Themes (à charger après les styles de base)
import './styles/themes/light-mode.css';
import './styles/themes/dark-mode.css';
// Thème Liquid Glass supprimé

// Layout styles (à charger après les thèmes)
import './styles/layout/responsive.css';
import './styles/layout/About.css';
import './styles/layout/Footer.css';

// Component styles (à charger en dernier)
import './styles/components/navigation.css';
import './styles/components/buttons.css';
import './styles/components/attractions.css';
import './styles/components/shows.css';
import './styles/components/skeleton.css';
import './styles/components/filters.css';
import './styles/components/forms.css';
import './styles/components/auth.css';
import './styles/components/modal.css';
// charts.css déplacé dans src/components/charts.js
import './styles/components/logo.css';
import './styles/components/theme-toggle.css';
import './styles/components/BottomNavigation.css';
import './styles/components/attraction-notification-modal.css';
import './styles/components/pass.css';
// EftelingPark.css déplacé dans src/Efteling.js
import './styles/components/tesla-splash.css';
import './styles/components/general-splash.css';
// PhantasialandPark.css déplacé dans src/Phantasialand.js
import './styles/components/restaurantsetservices.css';
import './styles/components/location.css';
import './styles/components/loading.css';
import './styles/components/back-to-top.css';
import './styles/components/mini-calendar.css';
import './styles/components/weather-widget.css';
import './styles/components/shareable-wait-times.css';
import './styles/components/calendar.css';
import './styles/components/opening-hours.css';
import './styles/components/news-block.css';
import './styles/components/ParksList.css';
import './styles/components/battery-banner.css';
import './styles/components/transport-badge.css';
import './styles/lcp-optimized.css';
import ErrorBoundary from './components/ErrorBoundary';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import LazyRoute from './components/LazyRoute';
import ParticleButton from './components/ParticleButton';
import BottomNavigation from './components/BottomNavigation';
import ParkMenu from './components/ParkMenu';
import NotFound from './components/NotFound';
import BackToTop from './components/BackToTop';
import GlobalNotifications from './components/GlobalNotifications';
import MaintenanceChecker from './components/MaintenanceChecker';
import { GraphProvider } from './contexts/GraphContext';
import { InfoButtonProvider } from './contexts/InfoButtonContext';
import { WDWProvider } from './contexts/WDWContext';
import { LocalTimeProvider } from './contexts/LocalTimeContext';
import { LiquidGlassProvider } from './contexts/LiquidGlassContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Footer from './components/Footer';
import ParkVideosSidebar from './components/ParkVideosSidebar';
import EpRulanticaLoader from './components/EpRulanticaLoader';
import DisneylandResortRestaurants from './DisneylandResortRestaurants';
import RequireAuth from './components/auth/RequireAuth';
import RequireAdmin from './components/auth/RequireAdmin';
import MemberAreaControl from './components/auth/MemberAreaControl';
import DispatchMaxQuickAccess from './components/DispatchMaxQuickAccess';
import { GENERIC_THEMEPARKS_CONTENT_PARKS, findGenericThemeparksContentPark, supportsGenericThemeparksMapToggle } from './utils/themeparksGenericContentConfig';
import { useThemeparksContentAvailability } from './utils/themeparksContentAvailability';
// SpringFlowers sera importé dynamiquement seulement si nécessaire

// DÉSACTIVÉ - À réactiver pour Noël prochain (novembre-décembre)
// const ChristmasLights = lazy(() => import('./components/ChristmasLights'));

const DisneylandPark = lazy(() => import('./DisneylandPark'));
const WaltDisneyStudios = lazy(() => import('./WaltDisneyStudios'));
const ShowsAndEvents = lazy(() => import('./ShowsAndEvents'));
const Restaurants = lazy(() => import('./Restaurants'));
const Efteling = lazy(() => import('./Efteling'));
const EftelingShows = lazy(() => import('./EftelingShows'));
const About = lazy(() => import('./components/About'));
const SupportUs = lazy(() => import('./SupportUs'));
const DispatchMax = lazy(() => import('./DispatchMax'));
const ContactForm = lazy(() => import('./components/ContactForm'));
const MyPass = lazy(() => import('./components/MyPass'));
const AsterixPass = lazy(() => import('./components/AsterixPass'));
const ParksList = lazy(() => import('./components/ParksList'));
// Snow sera importé dynamiquement seulement si nécessaire
const Phantasialand = lazy(() => import('./Phantasialand'));
const PhantasialandShows = lazy(() => import('./PhantasialandShows'));
const ParcAsterix = lazy(() => import('./ParcAsterix'));
const ParcAsterixShows = lazy(() => import('./ParcAsterixShows'));
const ParcAsterixRestaurants = lazy(() => import('./ParcAsterixRestaurants'));
const UniversalStudios = lazy(() => import('./UniversalStudios'));
const UniversalStudiosCalifornia = lazy(() => import('./UniversalStudiosCalifornia'));
const UniversalStudiosShows = lazy(() => import('./UniversalStudiosShows'));
const UniversalStudiosCaliforniaShows = lazy(() => import('./UniversalStudiosCaliforniaShows'));
const EpicUniverse = lazy(() => import('./EpicUniverse'));
const EpicUniverseShows = lazy(() => import('./EpicUniverseShows'));
const IslandsOfAdventure = lazy(() => import('./IslandsOfAdventure'));
const IslandsOfAdventureShows = lazy(() => import('./IslandsOfAdventureShows'));
const SeaWorldOrlando = lazy(() => import('./SeaWorldOrlando'));
const BuschGardensTampa = lazy(() => import('./BuschGardensTampa'));
const LegolandFlorida = lazy(() => import('./LegolandFlorida'));
const Toverland = lazy(() => import('./Toverland'));
const Futuroscope = lazy(() => import('./Futuroscope'));
const LePal = lazy(() => import('./LePal'));
const Energylandia = lazy(() => import('./Energylandia'));
const GronaLund = lazy(() => import('./GronaLund'));
const TVView = lazy(() => import('./components/TVView'));
const InteractiveMap = lazy(() => import('./components/InteractiveMap'));
const WalibiBelgium = lazy(() => import('./WalibiBelgium'));
const Nigloland = lazy(() => import('./Nigloland'));
const PlopsalandDePanne = lazy(() => import('./PlopsalandDePanne'));
const LegolandLoader = lazy(() => import('./components/LegolandLoader'));
const PortAventura = lazy(() => import('./PortAventura'));
const FerrariLand = lazy(() => import('./FerrariLand'));
const WalibiRhoneAlpes = lazy(() => import('./WalibiRhoneAlpes'));
const WalibiHolland = lazy(() => import('./WalibiHolland'));
const ThorpePark = lazy(() => import('./ThorpePark'));
const Bellewaerde = lazy(() => import('./Bellewaerde'));
const Bobbejaanland = lazy(() => import('./Bobbejaanland'));
const Gardaland = lazy(() => import('./Gardaland'));
const Mirabilandia = lazy(() => import('./Mirabilandia'));
const MovieParkGermany = lazy(() => import('./MovieParkGermany'));
const ParqueDeAtraccionesDeMadrid = lazy(() => import('./ParqueDeAtraccionesDeMadrid'));
const ShanghaiDisneyland = lazy(() => import('./ShanghaiDisneyland'));
const UniversalStudiosBeijing = lazy(() => import('./UniversalStudiosBeijing'));
const UniversalStudiosBeijingShows = lazy(() => import('./UniversalStudiosBeijingShows'));
const LotteWorld = lazy(() => import('./LotteWorld'));
// const SixFlagsQiddiyaCity = lazy(() => import('./SixFlagsQiddiyaCity'));
// const SixFlagsQiddiyaCityRestaurants = lazy(() => import('./SixFlagsQiddiyaCityRestaurants'));
const TokyoDisneyland = lazy(() => import('./TokyoDisneyland'));
const HongKongDisneylandPark = lazy(() => import('./HongKongDisneylandPark'));
const EuropaPark = lazy(() => import('./EuropaPark'));
const EuropaParkShows = lazy(() => import('./EuropaParkShows'));
const Rulantica = lazy(() => import('./Rulantica'));
const Traumatica = lazy(() => import('./Traumatica'));
const DisneylandCalifornia = lazy(() => import('./DisneylandCalifornia'));
const DisneyCaliforniaAdventure = lazy(() => import('./DisneyCaliforniaAdventure'));
const DisneylandCaliforniaShows = lazy(() => import('./DisneylandCaliforniaShows'));
const DisneyCaliforniaAdventureShows = lazy(() => import('./DisneyCaliforniaAdventureShows'));
const DisneylandResortShowsAndEvents = lazy(() => import('./DisneylandResortShowsAndEvents'));
const KnottBerryFarm = lazy(() => import('./components/KnottBerryFarm'));
const GenericThemeparkEntityPage = lazy(() => import('./components/parks/GenericThemeparkEntityPage'));
// Walt Disney World Resort
const MagicKingdom = lazy(() => import('./MagicKingdom'));
const MagicKingdomRestaurants = lazy(() => import('./MagicKingdomRestaurants'));
const MagicKingdomShows = lazy(() => import('./MagicKingdomShows'));
const Epcot = lazy(() => import('./Epcot'));
const EpcotRestaurants = lazy(() => import('./EpcotRestaurants'));
const EpcotShows = lazy(() => import('./EpcotShows'));
const HollywoodStudios = lazy(() => import('./HollywoodStudios'));
const HollywoodStudiosRestaurants = lazy(() => import('./HollywoodStudiosRestaurants'));
const HollywoodStudiosShows = lazy(() => import('./HollywoodStudiosShows'));
const AnimalKingdom = lazy(() => import('./AnimalKingdom'));
const AnimalKingdomRestaurants = lazy(() => import('./AnimalKingdomRestaurants'));
const AnimalKingdomShows = lazy(() => import('./AnimalKingdomShows'));
const TyphoonLagoon = lazy(() => import('./TyphoonLagoon'));
const BlizzardBeach = lazy(() => import('./BlizzardBeach'));
const LoginPage = lazy(() => import('./components/auth/LoginPage'));
const SignupPage = lazy(() => import('./components/auth/SignupPage'));
const ProfilePage = lazy(() => import('./components/auth/ProfilePage'));
const DashboardPage = lazy(() => import('./components/auth/DashboardPage'));
const EmailVerifiedPage = lazy(() => import('./components/auth/EmailVerifiedPage'));
const AuthCallbackPage = lazy(() => import('./components/auth/AuthCallbackPage'));
const AdminHomePage = lazy(() => import('./components/admin/AdminHomePage'));
const AdminNewsPage = lazy(() => import('./components/auth/AdminNewsPage'));
const AdminPredictionsPage = lazy(() => import('./components/admin/AdminPredictionsPage'));
const AdminCalibrationPage = lazy(() => import('./components/admin/AdminCalibrationPage'));
const AdminRunsPage = lazy(() => import('./components/admin/AdminRunsPage'));
const AdminAlertsPage = lazy(() => import('./components/admin/AdminAlertsPage'));
const AdminMaintenancePage = lazy(() => import('./components/admin/AdminMaintenancePage'));
const AdminMonitoringPage = lazy(() => import('./components/admin/AdminMonitoringPage'));
const DeanrellSoundbox = lazy(() => import('./components/deanrell-soundbox'));
const SplashScreenTest = lazy(() => import('./pages/SplashScreenTest'));

// Import des composants de parcs pour le routage dynamique
const createGenericParkComponent = (parkSlug, parkConfig) => {
  return lazy(() => Promise.all([
    import('./components/parks/BaseParkContent'),
    import('./hooks/useParkData'),
    import('./hooks/useSharedParkData'),
    import('./hooks/useDownReopenNotifications'),
    import('./config'),
    import('./components/LocationModal'),
    import('./components/HistoryGraphIcon'),
    import('./contexts/GraphContext'),
    import('lucide-react'),
    import('./utils/themeparksGenericData'),
    import('./utils/themeparksContentAvailability')
  ]).then(([baseParkModule, useParkDataModule, useSharedParkDataModule, useDownReopenModule, configModule, locationModalModule, historyGraphIconModule, graphContextModule, lucideModule, themeparksGenericDataModule, themeparksContentAvailabilityModule]) => {
    const BaseParkContent = baseParkModule.default;
    const useParkData = useParkDataModule.default;
    const useSharedParkData = useSharedParkDataModule.default;
    const useDownReopenNotifications = useDownReopenModule.default;
    const CONFIG_IMPORT = configModule.CONFIG;
    const LocationModal = locationModalModule.default;
    const HistoryGraphIcon = historyGraphIconModule.default;
    const GraphContext = graphContextModule.GraphContext;
    const { Star, MapPin } = lucideModule;
    const { buildThemeparksEntities, buildThemeparksMapEntities, buildThemeparksOpeningHours } = themeparksGenericDataModule;
    const { deriveThemeparksContentAvailability, writeThemeparksContentAvailability } = themeparksContentAvailabilityModule;

    const WaitTimeModalLazy = React.lazy(() => import('./components/WaitTimeModal'));

    const apiUrl = `${CONFIG_IMPORT.API_URL}/api/${parkSlug}/wait-times`;
    const cacheKey = `${parkSlug.replace(/-/g, '')}WaitTimes`;

    const GenericParkWrapper = (props) => {
      const { updateLoadingState } = props;
      const { t } = useTranslation();
      const { waitTimes: apiWaitTimes, lastUpdate, loading, dataStatus } =
        useParkData(apiUrl, cacheKey);
      const { favorites, toggleFavorite } = useSharedParkData();

      // États pour les modals
      const [selectedAttraction, setSelectedAttraction] = React.useState(null);
      const [selectedLocation, setSelectedLocation] = React.useState(null);

      // Contexte graphique
      const graphContext = React.useContext(GraphContext);
      const graphEnabled = graphContext?.graphEnabled !== false;

      // Transformer les données API en attractions
      const attractions = React.useMemo(() => {
        return buildThemeparksEntities(apiWaitTimes, parkConfig, ['ATTRACTION']);
      }, [apiWaitTimes]);

      const mapEntities = React.useMemo(() => {
        return buildThemeparksMapEntities(apiWaitTimes, parkConfig);
      }, [apiWaitTimes]);

      // Notifications de pannes / réouvertures
      useDownReopenNotifications({
        attractions: attractions || [],
        parkSlug: parkSlug
      });

      // Parser les horaires d'ouverture
      const openingHours = React.useMemo(() => {
        return buildThemeparksOpeningHours(apiWaitTimes);
      }, [apiWaitTimes]);

      React.useEffect(() => {
        if (loading || !apiWaitTimes || typeof apiWaitTimes !== 'object' || Array.isArray(apiWaitTimes)) {
          return;
        }

        writeThemeparksContentAvailability(
          parkSlug,
          deriveThemeparksContentAvailability(apiWaitTimes, parkConfig)
        );
      }, [apiWaitTimes, loading]);

      // Désactiver le loader global
      useEffect(() => {
        if (typeof updateLoadingState === 'function') {
          updateLoadingState(false);
        }
      }, [updateLoadingState]);

      // Fonctions utilitaires de status
      const getStatusText = React.useCallback((attraction) => {
        if (attraction.status === 'OPERATING') {
          return attraction.waitTime > 0 ? `${attraction.waitTime} min` : t('states.open');
        }
        switch (attraction.status) {
          case 'CLOSED': return t('states.closed');
          case 'DOWN': return t('states.outOfOrder');
          case 'REFURBISHMENT': return t('refurbishment');
          default: return t('noInfo');
        }
      }, [t]);

      const getStatusClass = React.useCallback((attraction) => {
        if (attraction.status !== 'OPERATING') {
          if (attraction.status === 'DOWN') return 'out-of-order';
          if (attraction.status === 'REFURBISHMENT') return 'refurbishment';
          return 'closed';
        }
        if (attraction.waitTime <= 5) return 'very-short-wait';
        if (attraction.waitTime <= 15) return 'short-wait';
        if (attraction.waitTime <= 30) return 'medium-wait';
        return 'long-wait';
      }, []);

      const getWaitTime = React.useCallback((attraction) => {
        return attraction.waitTime ?? 0;
      }, []);

      const formatTime = React.useCallback((dateInput) => {
        try {
          const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
          if (isNaN(date.getTime())) return '';
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
          return '';
        }
      }, []);

      const formatDate = React.useCallback((dateString) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString(undefined, {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
          });
        } catch {
          return dateString;
        }
      }, []);

      // Rendu d'un item d'attraction (enrichi avec graphique + localisation)
      const renderAttractionItem = React.useCallback((attraction) => {
        const isFav = favorites.includes(`${parkConfig.id}_${attraction.id}`);
        return (
          <div key={attraction.id} className="attraction-item">
            <div className="attraction-name-container">
              <h3 className="attraction-name">{attraction.name}</h3>
            </div>
            <div className="attraction-right">
              <div className="icons">
                {graphEnabled && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAttraction(attraction);
                    }}
                    className="icon-button"
                    aria-label="Voir l'historique"
                  >
                    <HistoryGraphIcon />
                  </button>
                )}
                {attraction.location && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLocation({
                        name: attraction.name,
                        location: attraction.location
                      });
                    }}
                    className="icon-button location-btn"
                    aria-label="Voir la localisation"
                  >
                    <MapPin className="location-icon" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(attraction.id, attraction.name, parkConfig.id);
                  }}
                  className="icon-button"
                  aria-label={isFav ? 'Remove favorite' : 'Add favorite'}
                >
                  <Star
                    className={`favorite-icon ${isFav ? 'active' : ''}`}
                    fill={isFav ? 'currentColor' : 'none'}
                  />
                </button>
              </div>
              <div className="wait-time-container">
                <span className={`wait-time ${getStatusClass(attraction)}`}>
                  {getStatusText(attraction)}
                </span>
              </div>
            </div>
          </div>
        );
      }, [favorites, toggleFavorite, getStatusClass, getStatusText, graphEnabled]);

      return (
        <>
          <BaseParkContent
            {...props}
            parkSlug={parkSlug}
            parkName={parkConfig.name}
            parkId={parkConfig.id}
            timeZone={parkConfig.timeZone}
            entityId={parkConfig.entityId}
            enableMapView={supportsGenericThemeparksMapToggle(parkSlug)}
            attractions={attractions}
            mapEntities={mapEntities}
            loading={loading}
            lastUpdate={lastUpdate}
            openingHours={openingHours}
            favorites={favorites}
            toggleFavorite={toggleFavorite}
            getStatusClass={getStatusClass}
            getStatusText={getStatusText}
            getWaitTime={getWaitTime}
            formatTime={formatTime}
            formatDate={formatDate}
            renderAttractionItem={renderAttractionItem}
            dataStatus={dataStatus}
            dataAttribution={
              <p>Data provided by <a href="https://api.themeparks.wiki" target="_blank" rel="noopener noreferrer">ThemeParks.wiki</a></p>
            }
          />
          {/* Modal graphique d'historique des temps d'attente */}
          {selectedAttraction && (
            <React.Suspense fallback={null}>
              <WaitTimeModalLazy
                isOpen={!!selectedAttraction}
                onClose={() => setSelectedAttraction(null)}
                attractionId={selectedAttraction.id}
                attractionName={selectedAttraction.name}
              />
            </React.Suspense>
          )}
          {/* Modal de localisation */}
          {selectedLocation && (
            <LocationModal
              isOpen={!!selectedLocation}
              onClose={() => setSelectedLocation(null)}
              attraction={selectedLocation}
              t={(key) => {
                const translations = {
                  closeModal: 'Fermer',
                  openInMaps: 'Ouvrir dans Maps',
                  openInPlan: 'Ouvrir dans Plans',
                  showLocation: 'Voir la localisation',
                  locationNotAvailable: 'Localisation non disponible'
                };
                return translations[key] || key;
              }}
            />
          )}
        </>
      );
    };
    return { default: GenericParkWrapper };
  }));
};


// Six Flags Qiddiya City - composant dédié avec toutes les fonctionnalités

// Nouveaux parcs - composants dédiés
const VolcanoBay = lazy(() => import('./VolcanoBay'));
const TokyoDisneySea = lazy(() => import('./TokyoDisneySea'));

// Composants génériques pour les 11 parcs standalone
const AltonTowers = createGenericParkComponent('alton-towers', { name: 'Alton Towers', id: 'ALT', timeZone: 'Europe/London', entityId: '0d8ea921-37b1-4a9a-b8ef-5b45afea847b' });
const UniversalStudiosSingapore = createGenericParkComponent('universal-studios-singapore', { name: 'Universal Studios Singapore', id: 'USS', timeZone: 'Asia/Singapore', entityId: 'f95d7f76-2024-4510-b799-26e122d0e448' });
const LegolandDeutschland = createGenericParkComponent('legoland-deutschland', { name: 'LEGOLAND Deutschland', id: 'LGD', timeZone: 'Europe/Berlin', entityId: 'e35cd439-ca3c-4234-ad92-d699995d0cba' });
const Carowinds = createGenericParkComponent('carowinds', { name: 'Carowinds', id: 'CRW', timeZone: 'America/New_York', entityId: '24cdcaa8-0500-4340-9725-992865eb18d6' });
const Kennywood = createGenericParkComponent('kennywood', { name: 'Kennywood', id: 'KNY', timeZone: 'America/New_York', entityId: '3dada5aa-0feb-4a3a-8c2d-685901f256be' });
const HeidePark = createGenericParkComponent('heide-park', { name: 'Heide Park', id: 'HDP', timeZone: 'Europe/Berlin', entityId: '66e12a41-3a09-40cd-8f55-8d335d9d7d93' });
const Knoebels = createGenericParkComponent('knoebels', { name: 'Knoebels Amusement Park', id: 'KNB', timeZone: 'America/New_York', entityId: 'fd747bb4-393f-4dff-afdd-9ee2533a1869' });
const SilverDollarCity = createGenericParkComponent('silver-dollar-city', { name: 'Silver Dollar City', id: 'SDC', timeZone: 'America/Chicago', entityId: 'd21fac4f-1099-4461-849c-0f8e0d6e85a6' });
const LaRonde = createGenericParkComponent('la-ronde', { name: 'La Ronde', id: 'LRD', timeZone: 'America/Toronto', entityId: 'd2bef7bc-f9fc-4272-a6f1-2539d7413911' });
const KingsIsland = createGenericParkComponent('kings-island', { name: 'Kings Island', id: 'KGI', timeZone: 'America/New_York', entityId: 'a0df8d87-7f72-4545-a58d-eb8aa76f914b' });
const Hersheypark = createGenericParkComponent('hersheypark', { name: 'Hersheypark', id: 'HSP', timeZone: 'America/New_York', entityId: '0f044655-cd94-4bb8-a8e3-c789f4eca787' });

// Mapping des continents
const CONTINENT_MAPPING = {
  'Europe': ['France', 'Netherlands', 'Germany', 'Belgium', 'Italy', 'Spain', 'Sweden', 'Poland', 'United Kingdom'],
  'Amerique': ['United States', 'Canada'],
  'Asie': ['Japan', 'China', 'South Korea', 'Saudi Arabia', 'Singapore']
};

// Fonction pour générer la description de la page d'accueil avec les parcs par continent
const generateHomeDescription = () => {
  const parksByContinent = {};

  // Grouper les parcs par continent
  Object.values(PARKS_CONFIG).forEach(park => {
    const continent = Object.keys(CONTINENT_MAPPING).find(cont =>
      CONTINENT_MAPPING[cont].includes(park.country)
    );
    if (continent) {
      if (!parksByContinent[continent]) {
        parksByContinent[continent] = [];
      }
      parksByContinent[continent].push(park.name);
    }
  });

  // Générer la description
  let description = "Consultez les temps d'attente en direct de 45+ parcs à thème : ";

  const continents = Object.keys(parksByContinent);
  continents.forEach((continent, index) => {
    const parks = parksByContinent[continent];
    const mainParks = parks.slice(0, 3); // Limiter à 3 parcs principaux par continent
    description += `${continent} (${mainParks.join(', ')}${parks.length > 3 ? '...' : ''})`;

    if (index < continents.length - 1) {
      description += ', ';
    }
  });

  description += ". Découvrez les temps d'attente en direct et planifiez votre visite dans les plus grands parcs d'attractions du monde.";
  return description;
};

// Configuration complète des 45 parcs
const PARKS_CONFIG = {
  // Parcs Disney (avec composants dédiés)
  'disneyland-paris': {
    name: 'Disneyland Paris',
    id: 'DLP',
    timeZone: 'Europe/Paris',
    entityId: 'dae968d5-630d-4719-8b06-3d107e944401',
    country: 'France',
    component: DisneylandPark
  },
  'walt-disney-studios': {
    name: 'Disney Adventure World',
    id: 'WDS',
    timeZone: 'Europe/Paris',
    entityId: 'ca888437-ebb4-4d50-aed2-d227f7096968',
    country: 'France',
    component: WaltDisneyStudios
  },
  'disneyland-california': {
    name: 'Disneyland California',
    id: 'DLC',
    timeZone: 'America/Los_Angeles',
    entityId: '7340550b-c14d-4def-80bb-acdb51d49a66',
    country: 'United States',
    component: DisneylandCalifornia
  },
  'disney-california-adventure': {
    name: 'Disney California Adventure',
    id: 'DCA',
    timeZone: 'America/Los_Angeles',
    entityId: 'b070cbc5-28d9-41e6-8a98-4d9e653e93fd',
    country: 'United States',
    component: DisneyCaliforniaAdventure
  },
  'magic-kingdom': {
    name: 'Magic Kingdom',
    id: 'MK',
    timeZone: 'America/New_York',
    entityId: '75ea578a-adc8-4116-a54d-dccb60765ef9',
    country: 'United States',
    component: MagicKingdom
  },
  'epcot': {
    name: 'Epcot',
    id: 'EP',
    timeZone: 'America/New_York',
    entityId: '47f90d2c-e191-4239-a466-5892ef59a88b',
    country: 'United States',
    component: Epcot
  },
  'hollywood-studios': {
    name: 'Hollywood Studios',
    id: 'HS',
    timeZone: 'America/New_York',
    entityId: '288747d1-8b4f-4a64-867e-ea7c9b27bad8',
    country: 'United States',
    component: HollywoodStudios
  },
  'animal-kingdom': {
    name: 'Animal Kingdom',
    id: 'AK',
    timeZone: 'America/New_York',
    entityId: '1c84a229-8862-4648-9c71-378ddd2c7693',
    country: 'United States',
    component: AnimalKingdom
  },
  'tokyo-disneyland': {
    name: 'Tokyo Disneyland',
    id: 'TDL',
    timeZone: 'Asia/Tokyo',
    entityId: '52db5e3e-e4e5-4e3d-8db0-4eff8f3febd0',
    country: 'Japan',
    useBaseParkContent: true
  },
  'shanghai-disneyland': {
    name: 'Shanghai Disneyland',
    id: 'SHDL',
    timeZone: 'Asia/Shanghai',
    entityId: 'b5c2a6f8-8e8e-4b8e-8b8e-8e8e4b8e8b8e',
    country: 'China',
    useBaseParkContent: true
  },
  'hong-kong-disneyland-park': {
    name: 'Hong Kong Disneyland',
    id: 'HKDL',
    timeZone: 'Asia/Hong_Kong',
    entityId: 'c2a6f8-8e8e-4b8e-8b8e-8e8e4b8e8b8e',
    country: 'China',
    useBaseParkContent: true
  },

  // Universal Studios (avec composants dédiés)
  'universal-studios': {
    name: 'Universal Studios Florida',
    id: 'USF',
    timeZone: 'America/New_York',
    entityId: 'e957da41-3552-4cf6-b636-5babc5cbc4e5',
    country: 'United States',
    component: UniversalStudios
  },
  'islands-of-adventure': {
    name: 'Islands of Adventure',
    id: 'IOA',
    timeZone: 'America/New_York',
    entityId: '288747d1-8b4f-4a64-867e-ea7c9b27bad8',
    country: 'United States',
    component: IslandsOfAdventure
  },
  'universal-studios-california': {
    name: 'Universal Studios California',
    id: 'USC',
    timeZone: 'America/Los_Angeles',
    entityId: 'c2a6f8-8e8e-4b8e-8b8e-8e8e4b8e8b8e',
    country: 'United States',
    component: UniversalStudiosCalifornia
  },

  // Parcs européens (utilisant BaseParkContent)
  'efteling': {
    name: 'Efteling',
    id: 'EFT',
    timeZone: 'Europe/Amsterdam',
    entityId: 'b070cbc5-28d9-41e6-8a98-4d9e653e93fd',
    country: 'Netherlands',
    useBaseParkContent: true
  },
  'phantasialand': {
    name: 'Phantasialand',
    id: 'PHA',
    timeZone: 'Europe/Berlin',
    entityId: '47f90d2c-e191-4239-a466-5892ef59a88b',
    country: 'Germany',
    useBaseParkContent: true
  },
  'europa-park': {
    name: 'Europa-Park',
    id: 'EUR',
    timeZone: 'Europe/Berlin',
    entityId: '75ea578a-adc8-4116-a54d-dccb60765ef9',
    country: 'Germany',
    useBaseParkContent: true
  },
  'toverland': {
    name: 'Toverland',
    id: 'TOV',
    timeZone: 'Europe/Amsterdam',
    entityId: '1c84a229-8862-4648-9c71-378ddd2c7693',
    country: 'Netherlands',
    useBaseParkContent: true
  },
  'walibi-belgium': {
    name: 'Walibi Belgium',
    id: 'WBE',
    timeZone: 'Europe/Brussels',
    entityId: '52db5e3e-e4e5-4e3d-8db0-4eff8f3febd0',
    country: 'Belgium',
    useBaseParkContent: true
  },
  'walibi-holland': {
    name: 'Walibi Holland',
    id: 'WNL',
    timeZone: 'Europe/Amsterdam',
    entityId: '7340550b-c14d-4def-80bb-acdb51d49a66',
    country: 'Netherlands',
    useBaseParkContent: true
  },
  'walibi-rhone-alpes': {
    name: 'Walibi Rhône-Alpes',
    id: 'WRA',
    timeZone: 'Europe/Paris',
    entityId: 'ca888437-ea3d-4e16-9f8d-8c83b62b4e0f',
    country: 'France',
    useBaseParkContent: true
  },
  'parc-asterix': {
    name: 'Parc Astérix',
    id: 'AST',
    timeZone: 'Europe/Paris',
    entityId: 'dae968d5-630d-4719-8b06-3d107e944401',
    country: 'France',
    useBaseParkContent: true
  },
  'futuroscope': {
    name: 'Futuroscope',
    id: 'FUT',
    timeZone: 'Europe/Paris',
    entityId: 'f111046c-f6e0-48f4-a1d2-bd9de612b980',
    country: 'France',
    useBaseParkContent: true
  },
  'le-pal': {
    name: 'Le Pal',
    id: 'PAL',
    timeZone: 'Europe/Paris',
    entityId: '47f90d2c-e191-4239-a466-5892ef59a88b',
    country: 'France',
    useBaseParkContent: true
  },
  'nigloland': {
    name: 'Nigloland',
    id: 'NIG',
    timeZone: 'Europe/Paris',
    entityId: '75ea578a-adc8-4116-a54d-dccb60765ef9',
    country: 'France',
    useBaseParkContent: true
  },
  'plopsaland-de-panne': {
    name: 'Plopsaland Belgium',
    id: 'PLP',
    timeZone: 'Europe/Brussels',
    entityId: 'f0ea9b9c-1ccb-4860-bfe6-b5aea7e4db2b',
    country: 'Belgium',
    useBaseParkContent: true
  },
  'gardaland': {
    name: 'Gardaland',
    id: 'GAR',
    timeZone: 'Europe/Rome',
    entityId: '52db5e3e-e4e5-4e3d-8db0-4eff8f3febd0',
    country: 'Italy',
    useBaseParkContent: true
  },
  'mirabilandia': {
    name: 'Mirabilandia',
    id: 'MIR',
    timeZone: 'Europe/Rome',
    entityId: '7340550b-c14d-4def-80bb-acdb51d49a66',
    country: 'Italy',
    useBaseParkContent: true
  },
  'movie-park-germany': {
    name: 'Movie Park Germany',
    id: 'MPG',
    timeZone: 'Europe/Berlin',
    entityId: 'ca888437-ea3d-4e16-9f8d-8c83b62b4e0f',
    country: 'Germany',
    useBaseParkContent: true
  },
  'parque-de-atracciones-de-madrid': {
    name: 'Parque de Atracciones de Madrid',
    id: 'PAM',
    timeZone: 'Europe/Madrid',
    entityId: 'dae968d5-630d-4719-8b06-3d107e944401',
    country: 'Spain',
    useBaseParkContent: true
  },
  'portaventura': {
    name: 'PortAventura',
    id: 'PTA',
    timeZone: 'Europe/Madrid',
    entityId: 'b070cbc5-28d9-41e6-8a98-4d9e653e93fd',
    country: 'Spain',
    useBaseParkContent: true
  },
  'ferrari-land': {
    name: 'Ferrari Land',
    id: 'FRL',
    timeZone: 'Europe/Madrid',
    entityId: '47f90d2c-e191-4239-a466-5892ef59a88b',
    country: 'Spain',
    useBaseParkContent: true
  },
  'energylandia': {
    name: 'Energylandia',
    id: 'ENE',
    timeZone: 'Europe/Warsaw',
    entityId: '75ea578a-adc8-4116-a54d-dccb60765ef9',
    country: 'Poland',
    useBaseParkContent: true
  },
  'grona-lund': {
    name: 'Gröna Lund',
    id: 'GRL',
    timeZone: 'Europe/Stockholm',
    entityId: '1c84a229-8862-4648-9c71-378ddd2c7693',
    country: 'Sweden',
    useBaseParkContent: true
  },
  'bellewaerde': {
    name: 'Bellewaerde',
    id: 'BEL',
    timeZone: 'Europe/Brussels',
    entityId: '52db5e3e-e4e5-4e3d-8db0-4eff8f3febd0',
    country: 'Belgium',
    useBaseParkContent: true
  },
  'bobbejaanland': {
    name: 'Bobbejaanland',
    id: 'BOB',
    timeZone: 'Europe/Brussels',
    entityId: '7340550b-c14d-4def-80bb-acdb51d49a66',
    country: 'Belgium',
    useBaseParkContent: true
  },

  // Parcs américains
  'seaworld-orlando': {
    name: 'SeaWorld Orlando',
    id: 'SWO',
    timeZone: 'America/New_York',
    entityId: 'ca888437-ea3d-4e16-9f8d-8c83b62b4e0f',
    country: 'United States',
    useBaseParkContent: true
  },
  'busch-gardens-tampa': {
    name: 'Busch Gardens Tampa',
    id: 'BGT',
    timeZone: 'America/New_York',
    entityId: 'dae968d5-630d-4719-8b06-3d107e944401',
    country: 'United States',
    useBaseParkContent: true
  },
  'legoland-florida': {
    name: 'LEGOLAND Florida',
    id: 'LFL',
    timeZone: 'America/New_York',
    entityId: 'b070cbc5-28d9-41e6-8a98-4d9e653e93fd',
    country: 'United States',
    useBaseParkContent: true
  },
  'knotts-berry-farm': {
    name: 'Knott\'s Berry Farm',
    id: 'KBF',
    timeZone: 'America/Los_Angeles',
    entityId: '47f90d2c-e191-4239-a466-5892ef59a88b',
    country: 'United States',
    useBaseParkContent: true
  },

  // Parcs asiatiques
  'lotte-world': {
    name: 'Lotte World',
    id: 'LTW',
    timeZone: 'Asia/Seoul',
    entityId: '75ea578a-adc8-4116-a54d-dccb60765ef9',
    country: 'South Korea',
    useBaseParkContent: true
  },
  'six-flags-qiddiyacity': {
    name: 'Six Flags Qiddiyacity',
    id: 'SFQ',
    timeZone: 'Asia/Riyadh',
    entityId: 'b070cbc5-28d9-41e6-8a98-4d9e653e93fd',
    country: 'Saudi Arabia',
    useBaseParkContent: true
  },

  // Nouveaux parcs - Europe
  'alton-towers': {
    name: 'Alton Towers',
    id: 'ALT',
    timeZone: 'Europe/London',
    entityId: '0d8ea921-37b1-4a9a-b8ef-5b45afea847b',
    country: 'United Kingdom',
    component: AltonTowers
  },
  'legoland-deutschland': {
    name: 'LEGOLAND Deutschland',
    id: 'LGD',
    timeZone: 'Europe/Berlin',
    entityId: 'e35cd439-ca3c-4234-ad92-d699995d0cba',
    country: 'Germany',
    component: LegolandDeutschland
  },
  'heide-park': {
    name: 'Heide Park',
    id: 'HDP',
    timeZone: 'Europe/Berlin',
    entityId: '66e12a41-3a09-40cd-8f55-8d335d9d7d93',
    country: 'Germany',
    component: HeidePark
  },

  // Nouveaux parcs - Amérique du Nord
  'volcano-bay': {
    name: "Universal's Volcano Bay",
    id: 'VCB',
    timeZone: 'America/New_York',
    entityId: 'fe78a026-b91b-470c-b906-9d2266b692da',
    country: 'United States',
    component: VolcanoBay
  },
  'carowinds': {
    name: 'Carowinds',
    id: 'CRW',
    timeZone: 'America/New_York',
    entityId: '24cdcaa8-0500-4340-9725-992865eb18d6',
    country: 'United States',
    component: Carowinds
  },
  'kennywood': {
    name: 'Kennywood',
    id: 'KNY',
    timeZone: 'America/New_York',
    entityId: '3dada5aa-0feb-4a3a-8c2d-685901f256be',
    country: 'United States',
    component: Kennywood
  },
  'knoebels': {
    name: 'Knoebels Amusement Park',
    id: 'KNB',
    timeZone: 'America/New_York',
    entityId: 'fd747bb4-393f-4dff-afdd-9ee2533a1869',
    country: 'United States',
    component: Knoebels
  },
  'silver-dollar-city': {
    name: 'Silver Dollar City',
    id: 'SDC',
    timeZone: 'America/Chicago',
    entityId: 'd21fac4f-1099-4461-849c-0f8e0d6e85a6',
    country: 'United States',
    component: SilverDollarCity
  },
  'la-ronde': {
    name: 'La Ronde',
    id: 'LRD',
    timeZone: 'America/Toronto',
    entityId: 'd2bef7bc-f9fc-4272-a6f1-2539d7413911',
    country: 'Canada',
    component: LaRonde
  },
  'kings-island': {
    name: 'Kings Island',
    id: 'KGI',
    timeZone: 'America/New_York',
    entityId: 'a0df8d87-7f72-4545-a58d-eb8aa76f914b',
    country: 'United States',
    component: KingsIsland
  },
  'hersheypark': {
    name: 'Hersheypark',
    id: 'HSP',
    timeZone: 'America/New_York',
    entityId: '0f044655-cd94-4bb8-a8e3-c789f4eca787',
    country: 'United States',
    component: Hersheypark
  },

  // Nouveaux parcs - Asie
  'universal-studios-singapore': {
    name: 'Universal Studios Singapore',
    id: 'USS',
    timeZone: 'Asia/Singapore',
    entityId: 'f95d7f76-2024-4510-b799-26e122d0e448',
    country: 'Singapore',
    component: UniversalStudiosSingapore
  },
  'tokyo-disneysea': {
    name: 'Tokyo DisneySea',
    id: 'TDS',
    timeZone: 'Asia/Tokyo',
    entityId: '67b290d5-3478-4f23-b601-2f8fb71ba803',
    country: 'Japan',
    component: TokyoDisneySea
  },
  'universal-studios-beijing': {
    name: 'Universal Studios Beijing',
    id: 'USB',
    timeZone: 'Asia/Shanghai',
    entityId: '68e1d8f0-ed42-4351-af25-160421e37ce0',
    country: 'China',
    useBaseParkContent: true
  },

  // Parcs aquatiques
  'typhoon-lagoon': {
    name: 'Typhoon Lagoon',
    id: 'TL',
    timeZone: 'America/New_York',
    entityId: '1c84a229-8862-4648-9c71-378ddd2c7693',
    country: 'United States',
    useBaseParkContent: true
  },
  'blizzard-beach': {
    name: 'Blizzard Beach',
    id: 'BB',
    timeZone: 'America/New_York',
    entityId: '52db5e3e-e4e5-4e3d-8db0-4eff8f3febd0',
    country: 'United States',
    useBaseParkContent: true
  },
  'rulantica': {
    name: 'Rulantica',
    id: 'RUL',
    timeZone: 'Europe/Berlin',
    entityId: '7340550b-c14d-4def-80bb-acdb51d49a66',
    country: 'Germany',
    useBaseParkContent: true
  },
  'epic-universe': {
    name: 'Epic Universe',
    id: 'EPU',
    timeZone: 'America/New_York',
    entityId: 'ca888437-ea3d-4e16-9f8d-8c83b62b4e0f',
    country: 'United States',
    useBaseParkContent: true
  }
};

const genericMainThemeparksComponentCache = new Map();

const getGenericMainThemeparksComponent = (parkSlug, parkConfig) => {
  if (!genericMainThemeparksComponentCache.has(parkSlug)) {
    genericMainThemeparksComponentCache.set(parkSlug, createGenericParkComponent(parkSlug, parkConfig));
  }

  return genericMainThemeparksComponentCache.get(parkSlug);
};

// Déterminer le slug de parc à utiliser pour l'API /api/park-videos
const getVideosParkSlug = (pathname) => {
  if (!pathname) return null;

  const path = pathname.toLowerCase();

  // Ne pas afficher la sidebar sur les vues TV
  if (path.includes('/tv')) return null;

  // Disneyland Paris (page d'accueil ou chemin dédié)
  if (path === '/' || path.includes('disneylandparis')) {
    return 'disneyland-park';
  }

  // Disney Adventure World (alias /studios)
  if (path === '/studios') {
    return 'walt-disney-studios';
  }

  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const firstSegment = segments[0];

  const EXCLUDED_SEGMENTS = new Set([
    'shows',
    'restaurants',
    'about',
    'support-us',
    'contact',
    'dispatch-max',
    'login',
    'signup',
    'profile',
    'dashboard',
    'admin'
  ]);

  if (EXCLUDED_SEGMENTS.has(firstSegment)) {
    return null;
  }

  // Utiliser directement le segment s'il correspond à un parc connu
  if (PARKS_CONFIG[firstSegment]) {
    return firstSegment;
  }

  return null;
};

// Fonction pour générer les données structurées JSON-LD TouristAttraction
// eslint-disable-next-line no-unused-vars
const generateStructuredData = (parkSlug) => {
  const parkConfig = PARKS_CONFIG[parkSlug];
  if (!parkConfig) return null;

  const coordinates = PARK_COORDINATES[parkSlug];
  if (!coordinates) return null;

  // Mapping des pays pour les codes ISO
  const countryCodes = {
    'France': 'FR',
    'Netherlands': 'NL',
    'Germany': 'DE',
    'Belgium': 'BE',
    'Italy': 'IT',
    'Spain': 'ES',
    'Sweden': 'SE',
    'Poland': 'PL',
    'United States': 'US',
    'Japan': 'JP',
    'China': 'CN',
    'South Korea': 'KR'
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": parkConfig.name,
    "description": `Découvrez ${parkConfig.name}, un parc d'attractions situé en ${parkConfig.country}. Temps d'attente en direct et informations pratiques sur Magic Park Times.`,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": countryCodes[parkConfig.country] || parkConfig.country
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": coordinates.lat,
      "longitude": coordinates.lng
    },
    "url": `${window.location.origin}/${parkSlug}`,
    "image": `${window.location.origin}/images/park-${parkSlug}.jpg`,
    "telephone": parkConfig.phone || undefined,
    "openingHours": parkConfig.openingHours || undefined
  };

  // Nettoyer les propriétés undefined
  Object.keys(structuredData).forEach(key => {
    if (structuredData[key] === undefined) {
      delete structuredData[key];
    }
  });

  return structuredData;
};

// Générateur automatique de routes pour tous les parcs
// eslint-disable-next-line no-unused-vars
const generateParkRoutes = ({ updateLoadingState, handleError, waitTimeThresholds } = {}) => {
  const routes = [];

  Object.entries(PARKS_CONFIG).forEach(([parkSlug, parkConfig]) => {
    let ParkComponent;

    // Utiliser le composant dédié si disponible
    if (parkConfig.component) {
      ParkComponent = parkConfig.component;
    }
    // Sinon utiliser BaseParkContent pour les parcs génériques
    else if (parkConfig.useBaseParkContent) {
      ParkComponent = createGenericParkComponent(parkSlug, parkConfig);
    }
    // Fallback pour les parcs non configurés
    else {
      return;
    }

    // Route principale du parc
    routes.push(
      <Route
        key={parkSlug}
        path={`/${parkSlug}`}
        element={
          <Suspense fallback={<div className="park-loading">Chargement de {parkConfig.name}...</div>}>
            <ParkComponent
              onError={handleError}
              updateLoadingState={updateLoadingState}
              waitTimeThresholds={waitTimeThresholds}
            />
          </Suspense>
        }
      />
    );

    // Route pour la vue TV du parc
    routes.push(
      <Route
        key={`${parkSlug}-tv`}
        path={`/${parkSlug}/tv`}
        element={
          <Suspense fallback={<div className="park-loading">Chargement de la vue TV...</div>}>
            <ParkComponent
              isTVView={true}
              onError={handleError}
              updateLoadingState={updateLoadingState}
              waitTimeThresholds={waitTimeThresholds}
            />
          </Suspense>
        }
      />
    );

    // Redirection des anciennes routes /map vers la page d'accueil du parc
    routes.push(
      <Route
        key={`${parkSlug}-map`}
        path={`/${parkSlug}/map`}
        element={
          <Navigate to={`/${parkSlug}`} replace />
        }
      />
    );
  });

  return routes;
};

// Configuration globale temporaire - maintenant contrôlée par le contexte
// window.REMAINING_TIME_ANALYSIS_ENABLED = false;

// Coordonnées des parcs
const PARK_COORDINATES = {
  dlp: { lat: 48.8722344, lng: 2.7758079 },
  wds: { lat: 48.8722344, lng: 2.7758079 },
  efteling: { lat: 51.6500, lng: 5.0474 },
  phantasialand: { lat: 50.7984, lng: 6.8799 },
  usf: { lat: 28.4744, lng: -81.4687 },
  ioa: { lat: 28.4741, lng: -81.4715 },
  epu: { lat: 28.4414, lng: -81.4487 },
  swo: { lat: 28.4115, lng: -81.4617 },
  bgt: { lat: 28.0372, lng: -82.4194 },
  lfl: { lat: 27.9894, lng: -81.6867 },
  tov: { lat: 51.3977, lng: 5.9827 },
  futuroscope: { lat: 46.6667, lng: 0.3667 },
  energylandia: { lat: 49.9706, lng: 19.2019 },
  gronaLund: { lat: 59.3233, lng: 18.0979 },
  walibiBelgium: { lat: 50.6961, lng: 4.5906 },
  nigloland: { lat: 48.259444, lng: 4.614444 },
  plopsalandDepanne: { lat: 51.0791, lng: 2.6020 },
  walibiRhoneAlpes: { lat: 45.6203, lng: 5.5717 },
  walibiHolland: { lat: 52.440140, lng: 5.767490 },
  disneylandCalifornia: { lat: 33.8121, lng: -117.9190 },
  disneyCaliforniaAdventure: { lat: 33.8068, lng: -117.9210 },
  // Walt Disney World Resort
  magicKingdom: { lat: 28.4177, lng: -81.5812 },
  epcot: { lat: 28.3747, lng: -81.5494 },
  hollywoodStudios: { lat: 28.3578, lng: -81.5584 },
  animalKingdom: { lat: 28.3587, lng: -81.5908 },
  typhoonLagoon: { lat: 28.3653, lng: -81.5358 },
  blizzardBeach: { lat: 28.3489, lng: -81.5748 },
  // Parcs européens supplémentaires
  parcAsterix: { lat: 49.1342, lng: 2.5707 },
  portaventura: { lat: 41.0864, lng: 1.1559 },
  ferrariland: { lat: 41.0864, lng: 1.1559 },
  gardaland: { lat: 45.4567, lng: 10.7139 },
  mirabilandia: { lat: 44.3381, lng: 12.2631 },
  europapark: { lat: 48.2661, lng: 7.7219 },
  rulantica: { lat: 48.2661, lng: 7.7219 },
  lePal: { lat: 46.5089, lng: 3.6350 },
  thorpePark: { lat: 51.4036, lng: -0.5125 },
  bellewaerde: { lat: 50.8472, lng: 2.9472 },
  bobbejaanland: { lat: 51.1978, lng: 4.9056 },
  movieParkGermany: { lat: 51.6236, lng: 6.9728 },
  parqueDeAtraccionesDeMadrid: { lat: 40.4114, lng: -3.7494 },
  // Parcs asiatiques
  tokyoDisneyland: { lat: 35.6329, lng: 139.8804 },
  tokyoDisneySea: { lat: 35.6267, lng: 139.8850 },
  shanghaiDisneyland: { lat: 31.1434, lng: 121.6580 },
  hongKongDisneyland: { lat: 22.3132, lng: 114.0413 },
  lotteWorld: { lat: 37.5111, lng: 127.0980 },
  universalStudiosBeijing: { lat: 39.8633, lng: 116.6800 },
  sixFlagsQiddiyacity: { lat: 24.58897640551116, lng: 46.332452533099726 },
  // Nouveaux parcs - Europe
  altonTowers: { lat: 52.9931, lng: -1.8947 },
  legolandDeutschland: { lat: 48.4249, lng: 10.3003 },
  heidePark: { lat: 53.0223, lng: 9.8724 },
  // Nouveaux parcs - Amérique du Nord
  volcanoBay: { lat: 28.4614, lng: -81.4723 },
  carowinds: { lat: 35.1006, lng: -80.9408 },
  kennywood: { lat: 40.3866, lng: -79.8619 },
  knoebels: { lat: 40.8800, lng: -76.5012 },
  silverDollarCity: { lat: 36.6042, lng: -93.2999 },
  laRonde: { lat: 45.5227, lng: -73.5342 },
  kingsIsland: { lat: 39.3443, lng: -84.2652 },
  hersheypark: { lat: 40.2878, lng: -76.6591 },
  // Nouveaux parcs - Asie
  universalStudiosSingapore: { lat: 1.2540, lng: 103.8239 }
};

const SIGNUP_BANNER_DISMISS_KEY = 'signupInviteBannerDismissedUntil';
const SIGNUP_BANNER_VISIT_KEY = 'signupVisitCount';
const SIGNUP_BANNER_SESSION_MARKER = 'signupVisitCountedThisSession';
const SIGNUP_BANNER_DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
const SIGNUP_BANNER_MIN_VISITS = 2;
const LEGACY_SITE_VISITS_KEY = 'site_visits';

const parseStorageArray = (value) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseStoredCount = (value) => {
  const parsedValue = parseInt(value || '0', 10);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

const AppWithRouter = ({
  isDarkMode,
  toggleTheme,
  isHighContrast,
  toggleHighContrast,
  showIOSRefresh,
  handleError,
  setWaitTimeThreshold,
  waitTimeThresholds,
  updateLoadingState,
  isLoading,
  isLoadingVisible,
  currentLanguage,
  isLanguageMenuOpen,
  setIsLanguageMenuOpen,
  changeLanguage,
  t,
  showSnow,
  toggleSnow,
  showInstallBanner,
  fireworksTime,
  setFireworksTime
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { authActionLoading, user, loading: authLoading } = useAuth();
  const [showSignupInviteBanner, setShowSignupInviteBanner] = useState(false);
  const [signupInviteBannerVariant, setSignupInviteBannerVariant] = useState('signup');
  // DÉSACTIVÉ - À réactiver pour Noël prochain
  // const [isChristmasLightsCssLoaded, setIsChristmasLightsCssLoaded] = useState(false);
  const [currentPark, setCurrentPark] = useState(null);
  const videosParkSlug = getVideosParkSlug(location.pathname);
  const genericThemeparksContentPark = findGenericThemeparksContentPark(location.pathname);
  const genericThemeparksContentAvailability = useThemeparksContentAvailability(
    genericThemeparksContentPark?.parkSlug || null,
    genericThemeparksContentPark
  );

  // Déterminer le parc actuel en fonction de l'URL
  useEffect(() => {
    const path = location.pathname.toLowerCase();

    if (path.includes('disneylandparis')) {
      setCurrentPark('dlp');
    } else if (path === '/shows') {
      setCurrentPark('dlp');
    } else if (path === '/studios' || path.includes('waltdisneystudios')) {
      setCurrentPark('wds');
    } else if (path.includes('efteling')) {
      setCurrentPark('efteling');
    } else if (path.includes('phantasialand')) {
      setCurrentPark('phantasialand');
    } else if (path.includes('universal-studios-california')) {
      setCurrentPark('universal-studios-california');
    } else if (path.includes('universal-studios-beijing')) {
      setCurrentPark('universalStudiosBeijing');
    } else if (path.includes('universal-studios')) {
      setCurrentPark('usf');
    } else if (path.includes('islands-of-adventure')) {
      setCurrentPark('ioa');
    } else if (path.includes('epic-universe')) {
      setCurrentPark('epu');
    } else if (path.includes('parc-asterix') || path.includes('asterix')) {
      setCurrentPark('parcAsterix');
    } else if (path.includes('portaventura')) {
      setCurrentPark('portaventura');
    } else if (path.includes('ferrariland')) {
      setCurrentPark('ferrariland');
    } else if (path.includes('gardaland')) {
      setCurrentPark('gardaland');
    } else if (path.includes('mirabilandia')) {
      setCurrentPark('mirabilandia');
    } else if (path.includes('europa-park') || path.includes('europapark')) {
      setCurrentPark('europapark');
    } else if (path.includes('rulantica')) {
      setCurrentPark('rulantica');
    } else if (path.includes('le-pal') || path.includes('lepal')) {
      setCurrentPark('lePal');
    } else if (path.includes('thorpe-park') || path.includes('thorpepark')) {
      setCurrentPark('thorpePark');
    } else if (path.includes('futuroscope')) {
      setCurrentPark('futuroscope');
    } else if (path.includes('nigloland')) {
      setCurrentPark('nigloland');
    } else if (path.includes('energylandia')) {
      setCurrentPark('energylandia');
    } else if (path.includes('grona-lund') || path.includes('gronalund')) {
      setCurrentPark('gronaLund');
    } else if (path.includes('walibi-belgium') || path.includes('walibibelgium')) {
      setCurrentPark('walibiBelgium');
    } else if (path.includes('walibi-rhone-alpes') || path.includes('walibirhonealpes')) {
      setCurrentPark('walibiRhoneAlpes');
    } else if (path.includes('walibi-holland') || path.includes('walibiholland')) {
      setCurrentPark('walibiHolland');
    } else if (path.includes('plopsaland')) {
      setCurrentPark('plopsalandDepanne');
    } else if (path.includes('toverland')) {
      setCurrentPark('tov');
    } else if (path.includes('bobbejaanland')) {
      setCurrentPark('bobbejaanland');
    } else if (path.includes('bellewaerde')) {
      setCurrentPark('bellewaerde');
    } else if (path.includes('movie-park-germany') || path.includes('movieparkgermany')) {
      setCurrentPark('movieParkGermany');
    } else if (path.includes('parque-de-atracciones') || path.includes('parquedeatracciones')) {
      setCurrentPark('parqueDeAtraccionesDeMadrid');
    } else if (path.includes('seaworld')) {
      setCurrentPark('swo');
    } else if (path.includes('busch-gardens') || path.includes('buschgardens')) {
      setCurrentPark('bgt');
    } else if (path.includes('legoland-florida') || path.includes('legolandflorida')) {
      setCurrentPark('lfl');
    } else if (path.includes('tokyo-disneyland')) {
      setCurrentPark('tokyoDisneyland');
    } else if (path.includes('tokyo-disneysea')) {
      setCurrentPark('tokyoDisneySea');
    } else if (path.includes('shanghai-disneyland')) {
      setCurrentPark('shanghaiDisneyland');
    } else if (path.includes('hong-kong-disneyland')) {
      setCurrentPark('hongKongDisneyland');
    } else if (path.includes('lotte-world') || path.includes('lotteworld')) {
      setCurrentPark('lotteWorld');
    } else if (path.includes('six-flags-qiddiyacity') || path.includes('sixflagsqiddiyacity')) {
      setCurrentPark('sixFlagsQiddiyacity');
    } else if (path.includes('alton-towers') || path.includes('altontowers')) {
      setCurrentPark('altonTowers');
    } else if (path.includes('universal-studios-singapore')) {
      setCurrentPark('universalStudiosSingapore');
    } else if (path.includes('volcano-bay') || path.includes('volcanobay')) {
      setCurrentPark('volcanoBay');
    } else if (path.includes('legoland-deutschland') || path.includes('legolanddeutschland')) {
      setCurrentPark('legolandDeutschland');
    } else if (path.includes('carowinds')) {
      setCurrentPark('carowinds');
    } else if (path.includes('kennywood')) {
      setCurrentPark('kennywood');
    } else if (path.includes('heide-park') || path.includes('heidepark')) {
      setCurrentPark('heidePark');
    } else if (path.includes('knoebels')) {
      setCurrentPark('knoebels');
    } else if (path.includes('silver-dollar-city') || path.includes('silverdollarcity')) {
      setCurrentPark('silverDollarCity');
    } else if (path.includes('la-ronde') || path.includes('laronde')) {
      setCurrentPark('laRonde');
    } else if (path.includes('kings-island') || path.includes('kingsisland')) {
      setCurrentPark('kingsIsland');
    } else if (path.includes('hersheypark')) {
      setCurrentPark('hersheypark');
    } else if (path.includes('magic-kingdom') || path.includes('magickingdom')) {
      setCurrentPark('magicKingdom');
    } else if (path.includes('epcot')) {
      setCurrentPark('epcot');
    } else if (path.includes('hollywood-studios') || path.includes('hollywoodstudios')) {
      setCurrentPark('hollywoodStudios');
    } else if (path.includes('animal-kingdom') || path.includes('animalkingdom')) {
      setCurrentPark('animalKingdom');
    } else if (path === '/') {
      setCurrentPark('dlp');
    } else {
      setCurrentPark(null);
    }
  }, [location]);

  // Filet de sécurité: forcer la disparition du loader après 8s si toujours visible
  useEffect(() => {
    const timer = setTimeout(() => {
      if (typeof updateLoadingState === 'function') {
        updateLoadingState(false);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [location.pathname, updateLoadingState]);

  useEffect(() => {
    const incrementVisitCountOncePerSession = () => {
      try {
        const storedVisits = parseStoredCount(localStorage.getItem(SIGNUP_BANNER_VISIT_KEY));
        const legacyVisits = parseStoredCount(localStorage.getItem(LEGACY_SITE_VISITS_KEY));
        const baselineVisitCount = Math.max(storedVisits, legacyVisits);

        if (baselineVisitCount !== storedVisits) {
          localStorage.setItem(SIGNUP_BANNER_VISIT_KEY, String(baselineVisitCount));
        }

        if (sessionStorage.getItem(SIGNUP_BANNER_SESSION_MARKER) === 'true') {
          return;
        }
        const nextVisitCount = baselineVisitCount > 0 ? baselineVisitCount + 1 : 1;
        localStorage.setItem(SIGNUP_BANNER_VISIT_KEY, String(nextVisitCount));
        sessionStorage.setItem(SIGNUP_BANNER_SESSION_MARKER, 'true');
      } catch {
        // ignore
      }
    };

    const computeShouldShowSignupBanner = () => {
      if (authLoading || showInstallBanner || user) {
        return false;
      }

      if (location.pathname === '/signup' || location.pathname === '/login' || location.pathname === '/dispatch-max') {
        return false;
      }

      const now = Date.now();
      const dismissedUntil = parseInt(localStorage.getItem(SIGNUP_BANNER_DISMISS_KEY) || '0', 10);
      if (Number.isFinite(dismissedUntil) && now < dismissedUntil) {
        return false;
      }

      const visitCount = Math.max(
        parseStoredCount(localStorage.getItem(SIGNUP_BANNER_VISIT_KEY)),
        parseStoredCount(localStorage.getItem(LEGACY_SITE_VISITS_KEY))
      );
      const attractionFavoritesCount = parseStorageArray(localStorage.getItem('allParksFavorites')).length;
      const parkFavoritesCount = parseStorageArray(localStorage.getItem('favoriteParkIds')).length;
      const totalFavoritesCount = attractionFavoritesCount + parkFavoritesCount;

      return (Number.isFinite(visitCount) && visitCount >= SIGNUP_BANNER_MIN_VISITS) || totalFavoritesCount >= 2;
    };

    const refreshSignupBannerVisibility = () => {
      const shouldShowBanner = computeShouldShowSignupBanner();
      if (shouldShowBanner) {
        const visitCount = Math.max(
          parseStoredCount(localStorage.getItem(SIGNUP_BANNER_VISIT_KEY)),
          parseStoredCount(localStorage.getItem(LEGACY_SITE_VISITS_KEY))
        );
        setSignupInviteBannerVariant(Number.isFinite(visitCount) && visitCount > 0 && visitCount % 2 === 0 ? 'dispatchMax' : 'signup');
      }
      setShowSignupInviteBanner(shouldShowBanner);
    };

    incrementVisitCountOncePerSession();
    refreshSignupBannerVisibility();

    window.addEventListener('mpt:favorites-updated', refreshSignupBannerVisibility);
    window.addEventListener('mpt:park-favorites-updated', refreshSignupBannerVisibility);
    window.addEventListener('storage', refreshSignupBannerVisibility);

    const cleanupSignedIn = onAuthEvent(AUTH_EVENTS.SIGNED_IN, refreshSignupBannerVisibility);
    const cleanupSignedOut = onAuthEvent(AUTH_EVENTS.SIGNED_OUT, refreshSignupBannerVisibility);

    return () => {
      window.removeEventListener('mpt:favorites-updated', refreshSignupBannerVisibility);
      window.removeEventListener('mpt:park-favorites-updated', refreshSignupBannerVisibility);
      window.removeEventListener('storage', refreshSignupBannerVisibility);
      cleanupSignedIn();
      cleanupSignedOut();
    };
  }, [authLoading, location.pathname, showInstallBanner, user]);

  const handleDismissSignupInviteBanner = useCallback(() => {
    try {
      localStorage.setItem(SIGNUP_BANNER_DISMISS_KEY, String(Date.now() + SIGNUP_BANNER_DISMISS_DURATION_MS));
    } catch {
      // ignore
    }
    setShowSignupInviteBanner(false);
  }, []);

  const handleSignupInviteClick = useCallback(() => {
    setShowSignupInviteBanner(false);
    navigate('/signup');
  }, [navigate]);

  const handleLoginInviteClick = useCallback(() => {
    setShowSignupInviteBanner(false);
    navigate('/login');
  }, [navigate]);

  const handleDispatchMaxInviteClick = useCallback(() => {
    setShowSignupInviteBanner(false);
    navigate('/dispatch-max');
  }, [navigate]);

  const isDispatchMaxRoute = location.pathname === '/dispatch-max';
  const isTvRoute = location.pathname.includes('/tv');
  const isMobileDevice = isMobile();
  const shouldShowHeaderLogo = !(isMobileDevice && isDispatchMaxRoute);
  const shouldShowMemberQuickAccess = !isTvRoute && !isDispatchMaxRoute;

  const renderNavigation = () => {
    // Ne pas afficher le view-selector sur mobile car nous avons la barre de navigation en bas
    if (isMobile()) {
      return null;
    }

    // Le sélecteur de vue n'est affiché que pour les pages Disney (Disneyland Park, Disney Adventure World, Shows et Restaurants)
    const isDizneyPage = location.pathname === '/' ||
      location.pathname === '/studios' ||
      location.pathname === '/shows' ||
      location.pathname === '/restaurants';

    // Le sélecteur de vue pour Universal Orlando Resort
    const isUniversalPage = location.pathname === '/universal-studios' ||
      location.pathname === '/islands-of-adventure' ||
      location.pathname === '/epic-universe' ||
      location.pathname === '/volcano-bay';

    if (isUniversalPage) {
      return (
        <nav className="view-selector universal-nav">
          <ParticleButton
            as={NavLink}
            to="/universal-studios"
            className="nav-button"
            activeclassname="active"
          >
            {t('navigation.usf')}
          </ParticleButton>
          <ParticleButton
            as={NavLink}
            to="/islands-of-adventure"
            className="nav-button"
            activeclassname="active"
          >
            {t('navigation.ioa')}
          </ParticleButton>
          <ParticleButton
            as={NavLink}
            to="/epic-universe"
            className="nav-button"
            activeclassname="active"
          >
            {t('navigation.epu')}
          </ParticleButton>
          <ParticleButton
            as={NavLink}
            to="/volcano-bay"
            className="nav-button"
            activeclassname="active"
          >
            Volcano Bay
          </ParticleButton>
        </nav>
      );
    }

    if (genericThemeparksContentPark) {
      const hasShows = genericThemeparksContentAvailability.hasShows;
      const hasRestaurants = genericThemeparksContentAvailability.hasRestaurants;

      if (!hasShows && !hasRestaurants) return null;

      return (
        <nav className="view-selector">
          <ParticleButton
            as={NavLink}
            to={genericThemeparksContentPark.path}
            className="nav-button"
            activeclassname="active"
            end
          >
            {t('navigation.attractions')}
          </ParticleButton>
          {hasShows && (
            <ParticleButton
              as={NavLink}
              to={`${genericThemeparksContentPark.path}/shows`}
              className="nav-button"
              activeclassname="active"
            >
              {t('filters.shows')}
            </ParticleButton>
          )}
          {hasRestaurants && (
            <ParticleButton
              as={NavLink}
              to={`${genericThemeparksContentPark.path}/restaurants`}
              className="nav-button"
              activeclassname="active"
            >
              {t('filters.restaurants')}
            </ParticleButton>
          )}
        </nav>
      );
    }

    if (!isDizneyPage) return null;

    return (
      <nav className="view-selector">
        <ParticleButton
          as={NavLink}
          to="/"
          className="nav-button"
          activeclassname="active"
          end
        >
          {t('navigation.disneylandPark')}
        </ParticleButton>
        <ParticleButton
          as={NavLink}
          to="/studios"
          className="nav-button"
          activeclassname="active"
        >
          {t('navigation.waltDisneyStudios')}
        </ParticleButton>
        <ParticleButton
          as={NavLink}
          to="/shows"
          className="nav-button"
          activeclassname="active"
        >
          {t('navigation.shows')}
        </ParticleButton>
        <ParticleButton
          as={NavLink}
          to="/restaurants"
          className="nav-button"
          activeclassname="active"
        >
          {t('navigation.restaurants')}
        </ParticleButton>
      </nav>
    );
  };

  const shouldShowSnow = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const startDate = new Date(currentYear, 10, 8); // 8 novembre
    const endDate = new Date(currentYear + 1, 0, 6); // 6 janvier année suivante

    // Affiche la neige si showSnow est true et si on est entre le 8 novembre et le 6 janvier
    return showSnow && currentDate >= startDate && currentDate <= endDate;
  };

  const shouldShowSnowButton = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const startDate = new Date(currentYear, 10, 8); // 8 novembre
    const endDate = new Date(currentYear + 1, 0, 6); // 6 janvier année suivante

    // Le bouton est visible entre le 8 novembre et le 6 janvier
    return currentDate >= startDate && currentDate <= endDate;
  };

  // DÉSACTIVÉ - À réactiver pour Noël prochain
  // const shouldShowChristmasLights = () => {
  //   const now = new Date();
  //   const month = now.getMonth();
  //   const day = now.getDate();
  //   // Afficher les guirlandes du 15 novembre au 6 janvier
  //   if (month === 10 && day >= 15) return true; // 15-30 novembre
  //   if (month === 11) return true; // Décembre
  //   if (month === 0 && day <= 6) return true; // 1-6 janvier
  //   return false;
  // };

  const isChristmasSeason = false; // Désactivé jusqu'à Noël prochain
  void isChristmasSeason;

  // DÉSACTIVÉ - À réactiver pour Noël prochain
  // useEffect(() => {
  //   if (!isChristmasSeason) return;
  //   let cancelled = false;

  //   import('./styles/components/christmas-lights.css')
  //     .then(() => {
  //       if (!cancelled) setIsChristmasLightsCssLoaded(true);
  //     })
  //     .catch(() => {
  //       if (!cancelled) setIsChristmasLightsCssLoaded(false);
  //     });

  //   return () => {
  //     cancelled = true;
  //   };
  // }, [isChristmasSeason]);

  return (
    <WeatherProvider
      parkCoordinates={currentPark ? PARK_COORDINATES[currentPark] : null}
      parkId={currentPark}
    >
      <SEOManager
        parksConfig={PARKS_CONFIG}
        homeDescription={generateHomeDescription()}
      />
      <div className={`app ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <ParkVideosSidebar parkSlug={videosParkSlug} lang={currentLanguage} />
        {/* DÉSACTIVÉ - À réactiver pour Noël prochain
        {isChristmasSeason && isChristmasLightsCssLoaded && (
          <Suspense fallback={null}>
            <ChristmasLights regenerateKey={location.pathname} />
          </Suspense>
        )}
        */}
        <div className="container">
          {shouldShowMemberQuickAccess && isMobileDevice && (
            <div className={`site-member-access ${isMobileDevice ? 'site-member-access-mobile' : 'site-member-access-desktop'}`}>
              <DispatchMaxQuickAccess />
              <MemberAreaControl />
            </div>
          )}
          {shouldShowSnow() && (
            <Suspense fallback={null}>
              {React.createElement(lazy(() => import('./components/Snow')))}
            </Suspense>
          )}
          <ParkMenu
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            isHighContrast={isHighContrast}
            toggleHighContrast={toggleHighContrast}
            currentLanguage={currentLanguage}
            isLanguageMenuOpen={isLanguageMenuOpen}
            setIsLanguageMenuOpen={setIsLanguageMenuOpen}
            changeLanguage={changeLanguage}
          />
          {!isMobileDevice && (
            <div className="theme-controls">
              {shouldShowSnowButton() && (
                <button
                  onClick={toggleSnow}
                  className="snow-toggle"
                  aria-label={showSnow ? "Désactiver la neige" : "Activer la neige"}
                >
                  {showSnow ? "❄️" : "☀️"}
                </button>
              )}
              <div className="theme-controls-right">
                {shouldShowMemberQuickAccess && <DispatchMaxQuickAccess />}
                {shouldShowMemberQuickAccess && <MemberAreaControl />}
                {showIOSRefresh && (
                  <button
                    type="button"
                    className="ios-pwa-refresh-button"
                    onClick={() => window.location.reload()}
                    aria-label={t('common.refresh', 'Rafraîchir la page')}
                    title={t('common.refresh', 'Rafraîchir la page')}
                  >
                    ↻
                  </button>
                )}
              </div>
            </div>
          )}
          {shouldShowHeaderLogo && <HeaderLogo isDarkMode={isDarkMode} />}

          {renderNavigation()}

          {(isLoading || isLoadingVisible) && (
            <div className={`loading-overlay ${isLoading ? 'visible' : 'hidden'}`}>
              {location.pathname.includes('/efteling') ? (
                <div className="efteling-loading">
                  <div className="efteling-line"></div>
                </div>
              ) : location.pathname.includes('/parc-asterix') ? (
                <div className="asterix-loading">
                  <img src="/images/loading-idefix.svg" alt="Idéfix chargement" />
                </div>
              ) : location.pathname.includes('/phantasialand') ? (
                <div className="phantasialand-loading">
                  <div className="train-container">
                    <img src="/images/taron-loading.png" alt="Taron wagon 1" className="wagon" />
                    <img src="/images/taron-loading.png" alt="Taron wagon 2" className="wagon" />
                    <img src="/images/taron-loading.png" alt="Taron wagon 3" className="wagon" />
                    <img src="/images/taron-loading.png" alt="Taron wagon 4" className="wagon" />
                  </div>
                </div>
              ) : location.pathname.includes('/le-pal') ? (
                <div className="le-pal-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-lepal.webp" type="image/webp" />
                    <img src="/images/loader-lepal.png" alt="Le Pal Logo" className="le-pal-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/energylandia') ? (
                <div className="energylandia-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-el.webp" type="image/webp" />
                    <img src="/images/loader-el.png" alt="Energylandia Logo" className="energylandia-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/grona-lund') ? (
                <div className="grona-lund-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-gronalund.webp" type="image/webp" />
                    <img src="/images/loader-gronalund.png" alt="Gröna Lund Logo" className="grona-lund-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/toverland') ? (
                <div className="toverland-loading">
                  <div className="magic-wand">
                    <div className="wand-tip"></div>
                  </div>
                  <div className="magic-stars">
                    <div className="magic-star">✨</div>
                    <div className="magic-star">✨</div>
                    <div className="magic-star">✨</div>
                    <div className="magic-star">✨</div>
                    <div className="magic-star">✨</div>
                  </div>
                  <div className="magic-amulet">
                    <div className="amulet-symbol">✦</div>
                  </div>
                </div>
              ) : location.pathname.includes('/nigloland') ? (
                <div className="nigloland-loading">
                  <img src="/images/niglo-loading.png" alt="Perroquet pirate Nigloland" className="niglo-mascot" />
                </div>
              ) : location.pathname.includes('/universal-studios') ? (
                <div className="universal-loading">
                  <img src="/images/universal-studiosFL-loading.png" alt="Universal Studios Florida Logo" className="universal-logo" />
                </div>
              ) : location.pathname.includes('/universal-studios-california') ? (
                <div className="universal-loading">
                  <img src="/images/universal-studiosFL-loading.png" alt="Universal Studios California Logo" className="universal-logo" />
                </div>
              ) : location.pathname.includes('/islands-of-adventure') ? (
                <div className="universal-loading">
                  <img src="/images/universal-studiosFL-loading.png" alt="Islands of Adventure Logo" className="universal-logo" />
                </div>
              ) : location.pathname.includes('/epic-universe') ? (
                <div className="universal-loading">
                  <img src="/images/universal-studiosFL-loading.png" alt="Epic Universe Logo" className="universal-logo" />
                </div>
              ) : location.pathname.includes('/seaworld-orlando') ? (
                <div className="seaworld-loading">
                  <div className="circle">
                    <div className="wave"></div>
                  </div>
                </div>
              ) : location.pathname.includes('/busch-gardens-tampa') ? (
                <div className="busch-loading">
                  <img src="/images/bush-loading.png" alt="Busch Gardens Tampa Logo" className="busch-logo" />
                </div>
              ) : location.pathname.includes('/legoland-florida') ? (
                <LegolandLoader />
              ) : location.pathname.includes('/europa-park') || location.pathname.includes('/rulantica') || location.pathname.includes('/traumatica') ? (
                <EpRulanticaLoader />
              ) : location.pathname.includes('/plopsaland-de-panne') ? (
                <div className="plopsaland-loading">
                  <img src="/images/logo-plopsapanne.png" alt="Plopsaland Belgium Logo" className="plopsaland-logo" />
                </div>
              ) : location.pathname.includes('/portaventura') ? (
                <div className="portaventura-loading">
                  <img
                    src="/images/portaventuraworld-loading.png"
                    alt="PortAventura World Logo"
                    className="portaventura-logo"
                  />
                </div>
              ) : location.pathname.includes('/portaventura/ferrari-land') ? (
                <div className="ferrari-loading">
                  <img src="/images/ferrari-loading.png" alt="Ferrari Land Logo" className="ferrari-logo" />
                </div>
              ) : location.pathname.includes('/walibi-rhone-alpes') ? (
                <div className="walibi-loading">
                  <img src="/images/walibi-loading.png" alt="Walibi Logo" className="walibi-logo" />
                </div>
              ) : location.pathname.includes('/walibi-belgium') ? (
                <div className="walibi-loading">
                  <img src="/images/walibi-loading.png" alt="Walibi Logo" className="walibi-logo" />
                </div>
              ) : location.pathname.includes('/walibi-holland') ? (
                <div className="walibi-holland-loading">
                  <img src="/images/walibi-loading.png" alt="Walibi Logo" className="walibi-logo" />
                </div>
              ) : location.pathname.includes('/thorpe-park') ? (
                <div className="thorpe-park-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-thorpe-park.webp" type="image/webp" />
                    <img src="/images/loader-thorpe-park.png" alt="Thorpe Park Logo" className="thorpe-park-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/bellewaerde') ? (
                <div className="bellewaerde-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-bellewaerde.webp" type="image/webp" />
                    <img src="/images/loader-bellewaerde.png" alt="Bellewaerde Logo" className="bellewaerde-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/bobbejaanland') ? (
                <div className="bobbejaanland-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-bobbejaanland.webp" type="image/webp" />
                    <img src="/images/loader-bobbejaanland.png" alt="Bobbejaanland Logo" className="bobbejaanland-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/gardaland') ? (
                <div className="gardaland-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-gardaland.webp" type="image/webp" />
                    <img src="/images/loader-gardaland.png" alt="Gardaland Logo" className="gardaland-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/mirabilandia') ? (
                <div className="mirabilandia-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-mirabilandia.webp" type="image/webp" />
                    <img src="/images/loader-mirabilandia.png" alt="Mirabilandia Logo" className="mirabilandia-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/movie-park-germany') ? (
                <div className="movie-park-germany-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-movie-park-germany.webp" type="image/webp" />
                    <img src="/images/loader-movie-park-germany.png" alt="Movie Park Germany Logo" className="movie-park-germany-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/parque-de-atracciones-de-madrid') ? (
                <div className="parque-de-atracciones-de-madrid-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-parque-de-atracciones-de-madrid.webp" type="image/webp" />
                    <img src="/images/loader-parque-de-atracciones-de-madrid.png" alt="Parque de Atracciones de Madrid Logo" className="parque-de-atracciones-de-madrid-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/shanghai-disneyland') ? (
                <div className="mickey-loading">
                  <div className="ear ear-left"></div>
                  <div className="ear ear-right"></div>
                  <div className="head"></div>
                </div>
              ) : location.pathname.includes('/universal-studios-beijing') ? (
                <div className="universal-loading">
                  <img src="/images/universal-studiosFL-loading.png" alt="Universal Studios Beijing Logo" className="universal-logo" />
                </div>
              ) : location.pathname.includes('/lotte-world') ? (
                <div className="lotte-world-loading">
                  <picture>
                    <source srcSet="/images/webp/loader-Lotteworld.webp" type="image/webp" />
                    <img src="/images/loader-lotteworld.png" alt="Lotte World Logo" className="lotte-world-logo" />
                  </picture>
                </div>
              ) : location.pathname.includes('/tokyo-disneyland') ? (
                <div className="mickey-loading">
                  <div className="ear ear-left"></div>
                  <div className="ear ear-right"></div>
                  <div className="head"></div>
                </div>
              ) : location.pathname.includes('/hong-kong-disneyland-park') ? (
                <div className="mickey-loading">
                  <div className="ear ear-left"></div>
                  <div className="ear ear-right"></div>
                  <div className="head"></div>
                </div>
              ) : location.pathname.includes('/knotts-berry-farm') ? (
                <div className="knotts-loading">
                  <img src="/images/knotts-loader.png" alt="Knott's Berry Farm Logo" className="knotts-logo" />
                </div>
              ) : (
                <div className="mickey-loading">
                  <div className="ear ear-left"></div>
                  <div className="ear ear-right"></div>
                  <div className="head"></div>
                </div>
              )}
            </div>
          )}

          {authActionLoading && (
            <div className="auth-operation-overlay" role="status" aria-live="polite" aria-busy="true">
              <div className="auth-operation-loader" aria-hidden="true" />
              <span className="auth-operation-text">Synchronisation du compte...</span>
            </div>
          )}

          <Suspense fallback={null}>
            <Routes>
              <Route
                path="/"
                element={
                  <DisneylandPark
                    onError={handleError}
                    setWaitTimeThreshold={setWaitTimeThreshold}
                    waitTimeThresholds={waitTimeThresholds}
                    updateLoadingState={updateLoadingState}
                    fireworksTime={fireworksTime}
                  />
                }
              />
              <Route path="/tv" element={<TVView updateLoadingState={updateLoadingState} />} />
              <Route path="/map" element={
                <ErrorBoundary onError={handleError}>
                  <InteractiveMap updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/studios" element={<WaltDisneyStudios onError={handleError} setWaitTimeThreshold={setWaitTimeThreshold} waitTimeThresholds={waitTimeThresholds} updateLoadingState={updateLoadingState} />} />
              <Route path="/studios/tv" element={<TVView updateLoadingState={updateLoadingState} />} />
              {/* Route générique pour la vue TV de tous les parcs */}
              <Route path="/:parkSlug/tv" element={<TVView updateLoadingState={updateLoadingState} />} />
              <Route
                path="/shows"
                element={
                  <LazyRoute>
                    <ShowsAndEvents
                      updateLoadingState={updateLoadingState}
                      onFireworksTimeUpdate={setFireworksTime}
                    />
                  </LazyRoute>
                }
              />
              <Route path="/restaurants" element={<Restaurants updateLoadingState={updateLoadingState} />} />
              <Route path="/efteling" element={<Efteling onError={handleError} waitTimeThresholds={waitTimeThresholds} updateLoadingState={updateLoadingState} />} />
              <Route path="/efteling/shows" element={
                <ErrorBoundary onError={handleError}>
                  <EftelingShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/efteling/tv" element={<TVView updateLoadingState={updateLoadingState} />} />
              <Route path="/about" element={<About updateLoadingState={updateLoadingState} />} />
              <Route path="/email-verified" element={<EmailVerifiedPage updateLoadingState={updateLoadingState} />} />
              <Route path="/auth/callback" element={<AuthCallbackPage updateLoadingState={updateLoadingState} />} />
              <Route path="/login" element={<LoginPage updateLoadingState={updateLoadingState} />} />
              <Route path="/signup" element={<SignupPage updateLoadingState={updateLoadingState} />} />
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <AdminHomePage updateLoadingState={updateLoadingState} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/news"
                element={
                  <RequireAdmin>
                    <AdminNewsPage updateLoadingState={updateLoadingState} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/predictions"
                element={
                  <RequireAdmin>
                    <AdminPredictionsPage updateLoadingState={updateLoadingState} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/calibration"
                element={
                  <RequireAdmin>
                    <AdminCalibrationPage updateLoadingState={updateLoadingState} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/runs"
                element={
                  <RequireAdmin>
                    <AdminRunsPage updateLoadingState={updateLoadingState} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/alerts"
                element={
                  <RequireAdmin>
                    <AdminAlertsPage updateLoadingState={updateLoadingState} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/maintenance"
                element={
                  <RequireAdmin>
                    <AdminMaintenancePage updateLoadingState={updateLoadingState} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/monitoring"
                element={
                  <RequireAdmin>
                    <AdminMonitoringPage updateLoadingState={updateLoadingState} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/profile"
                element={
                  <RequireAuth>
                    <ProfilePage updateLoadingState={updateLoadingState} />
                  </RequireAuth>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <DashboardPage updateLoadingState={updateLoadingState} />
                  </RequireAuth>
                }
              />
              <Route
                path="/dispatch-max"
                element={
                  <ErrorBoundary onError={handleError}>
                    <DispatchMax updateLoadingState={updateLoadingState} />
                  </ErrorBoundary>
                }
              />
              <Route path="/my-pass" element={<MyPass updateLoadingState={updateLoadingState} />} />
              <Route path="/asterix-pass" element={
                <ErrorBoundary onError={handleError}>
                  <AsterixPass updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              {GENERIC_THEMEPARKS_CONTENT_PARKS
                .filter(({ parkSlug }) => supportsGenericThemeparksMapToggle(parkSlug))
                .map(({ parkSlug, path }) => {
                  const parkConfig = PARKS_CONFIG[parkSlug];

                  if (!parkConfig) {
                    return null;
                  }

                  const GenericMainParkComponent = getGenericMainThemeparksComponent(parkSlug, parkConfig);

                  return (
                    <Route
                      key={`${parkSlug}-generic-main-route`}
                      path={path}
                      element={
                        <ErrorBoundary onError={handleError}>
                          <GenericMainParkComponent
                            onError={handleError}
                            updateLoadingState={updateLoadingState}
                            waitTimeThresholds={waitTimeThresholds}
                          />
                        </ErrorBoundary>
                      }
                    />
                  );
                })}
              <Route path="/phantasialand" element={
                <LazyRoute>
                  <Phantasialand onError={handleError} waitTimeThresholds={waitTimeThresholds} updateLoadingState={updateLoadingState} />
                </LazyRoute>
              } />
              <Route path="/phantasialand/shows" element={
                <ErrorBoundary onError={handleError}>
                  <PhantasialandShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/phantasialand/tv" element={<TVView updateLoadingState={updateLoadingState} />} />
              <Route path="/parc-asterix" element={<ParcAsterix onError={handleError} waitTimeThresholds={waitTimeThresholds} updateLoadingState={updateLoadingState} />} />
              <Route path="/parc-asterix/tv" element={<TVView updateLoadingState={updateLoadingState} />} />
              <Route path="/parc-asterix/shows" element={
                <ErrorBoundary onError={handleError}>
                  <ParcAsterixShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/parc-asterix/restaurants" element={
                <ErrorBoundary onError={handleError}>
                  <ParcAsterixRestaurants updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/universal-studios" element={
                <ErrorBoundary onError={handleError}>
                  <UniversalStudios onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/universal-studios/shows" element={
                <ErrorBoundary onError={handleError}>
                  <UniversalStudiosShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/universal-studios/tv" element={<TVView updateLoadingState={updateLoadingState} />} />
              <Route path="/universal-studios-california" element={
                <ErrorBoundary onError={handleError}>
                  <UniversalStudiosCalifornia onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/universal-studios-california/shows" element={
                <ErrorBoundary onError={handleError}>
                  <UniversalStudiosCaliforniaShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/universal-studios-california/tv" element={<TVView updateLoadingState={updateLoadingState} />} />
              <Route path="/islands-of-adventure" element={
                <ErrorBoundary onError={handleError}>
                  <IslandsOfAdventure onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/islands-of-adventure/shows" element={
                <ErrorBoundary onError={handleError}>
                  <IslandsOfAdventureShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/islands-of-adventure/tv" element={<TVView updateLoadingState={updateLoadingState} />} />
              <Route path="/epic-universe" element={
                <ErrorBoundary onError={handleError}>
                  <EpicUniverse onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/epic-universe/shows" element={
                <ErrorBoundary onError={handleError}>
                  <EpicUniverseShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/seaworld-orlando" element={
                <ErrorBoundary onError={handleError}>
                  <SeaWorldOrlando onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/busch-gardens-tampa" element={
                <ErrorBoundary onError={handleError}>
                  <BuschGardensTampa onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/disneyland-california" element={
                <ErrorBoundary onError={handleError}>
                  <DisneylandCalifornia updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/disneyland-california/shows" element={
                <ErrorBoundary onError={handleError}>
                  <DisneylandCaliforniaShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/disney-california-adventure" element={
                <ErrorBoundary onError={handleError}>
                  <DisneyCaliforniaAdventure updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/disney-california-adventure/shows" element={
                <ErrorBoundary onError={handleError}>
                  <DisneyCaliforniaAdventureShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/disneyland-resort/shows" element={
                <LazyRoute>
                  <ErrorBoundary onError={handleError}>
                    <DisneylandResortShowsAndEvents updateLoadingState={updateLoadingState} />
                  </ErrorBoundary>
                </LazyRoute>
              } />
              <Route path="/disneyland-resort/restaurants" element={
                <ErrorBoundary onError={handleError}>
                  <DisneylandResortRestaurants updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/legoland-florida" element={
                <ErrorBoundary onError={handleError}>
                  <LegolandFlorida onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/toverland" element={
                <ErrorBoundary onError={handleError}>
                  <Toverland onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/futuroscope" element={
                <ErrorBoundary onError={handleError}>
                  <Futuroscope onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/le-pal" element={
                <ErrorBoundary onError={handleError}>
                  <LePal onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/energylandia" element={
                <ErrorBoundary onError={handleError}>
                  <Energylandia onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/grona-lund" element={
                <ErrorBoundary onError={handleError}>
                  <GronaLund onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/walibi-belgium" element={
                <ErrorBoundary onError={handleError}>
                  <WalibiBelgium onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/nigloland" element={
                <ErrorBoundary onError={handleError}>
                  <Nigloland onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/plopsaland-de-panne" element={
                <ErrorBoundary onError={handleError}>
                  <PlopsalandDePanne onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/portaventura" element={
                <ErrorBoundary onError={handleError}>
                  <PortAventura onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/portaventura/ferrari-land" element={
                <ErrorBoundary onError={handleError}>
                  <FerrariLand onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/portaventura/ferrari-land/tv" element={<TVView updateLoadingState={updateLoadingState} />} />
              <Route path="/walibi-rhone-alpes" element={
                <ErrorBoundary onError={handleError}>
                  <WalibiRhoneAlpes onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/walibi-holland" element={
                <ErrorBoundary onError={handleError}>
                  <WalibiHolland onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/thorpe-park" element={
                <ErrorBoundary onError={handleError}>
                  <ThorpePark onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/bellewaerde" element={
                <ErrorBoundary onError={handleError}>
                  <Bellewaerde onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/bobbejaanland" element={
                <ErrorBoundary onError={handleError}>
                  <Bobbejaanland onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/gardaland" element={
                <ErrorBoundary onError={handleError}>
                  <Gardaland onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/mirabilandia" element={
                <ErrorBoundary onError={handleError}>
                  <Mirabilandia onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/movie-park-germany" element={
                <ErrorBoundary onError={handleError}>
                  <MovieParkGermany onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/parque-de-atracciones-de-madrid" element={
                <ErrorBoundary onError={handleError}>
                  <ParqueDeAtraccionesDeMadrid onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/shanghai-disneyland" element={
                <ErrorBoundary onError={handleError}>
                  <ShanghaiDisneyland onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/universal-studios-beijing" element={
                <ErrorBoundary onError={handleError}>
                  <UniversalStudiosBeijing onError={handleError} updateLoadingState={updateLoadingState} enableShowsSection={false} />
                </ErrorBoundary>
              } />
              <Route path="/universal-studios-beijing/shows" element={
                <ErrorBoundary onError={handleError}>
                  <UniversalStudiosBeijingShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/lotte-world" element={
                <ErrorBoundary onError={handleError}>
                  <LotteWorld onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/tokyo-disneyland" element={
                <ErrorBoundary onError={handleError}>
                  <TokyoDisneyland onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/hong-kong-disneyland-park" element={
                <ErrorBoundary onError={handleError}>
                  <HongKongDisneylandPark onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/europa-park" element={
                <ErrorBoundary onError={handleError}>
                  <EuropaPark onError={handleError} updateLoadingState={updateLoadingState} waitTimeThresholds={waitTimeThresholds} />
                </ErrorBoundary>
              } />
              <Route path="/europa-park/shows" element={
                <ErrorBoundary onError={handleError}>
                  <EuropaParkShows onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/rulantica" element={
                <ErrorBoundary onError={handleError}>
                  <Rulantica onError={handleError} updateLoadingState={updateLoadingState} waitTimeThresholds={waitTimeThresholds} />
                </ErrorBoundary>
              } />
              <Route path="/traumatica" element={
                <ErrorBoundary onError={handleError}>
                  <Traumatica onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/knotts-berry-farm" element={<KnottBerryFarm onError={handleError} updateLoadingState={updateLoadingState} />} />
              {/* Walt Disney World Resort */}
              <Route path="/magic-kingdom" element={
                <ErrorBoundary onError={handleError}>
                  <MagicKingdom onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/magic-kingdom/shows" element={
                <ErrorBoundary onError={handleError}>
                  <MagicKingdomShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/magic-kingdom/restaurants" element={
                <ErrorBoundary onError={handleError}>
                  <MagicKingdomRestaurants updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/epcot" element={
                <ErrorBoundary onError={handleError}>
                  <Epcot onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/epcot/shows" element={
                <ErrorBoundary onError={handleError}>
                  <EpcotShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/epcot/restaurants" element={
                <ErrorBoundary onError={handleError}>
                  <EpcotRestaurants updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/hollywood-studios" element={
                <ErrorBoundary onError={handleError}>
                  <HollywoodStudios onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/hollywood-studios/shows" element={
                <ErrorBoundary onError={handleError}>
                  <HollywoodStudiosShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/hollywood-studios/restaurants" element={
                <ErrorBoundary onError={handleError}>
                  <HollywoodStudiosRestaurants updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/animal-kingdom" element={
                <ErrorBoundary onError={handleError}>
                  <AnimalKingdom onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/animal-kingdom/shows" element={
                <ErrorBoundary onError={handleError}>
                  <AnimalKingdomShows updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/animal-kingdom/restaurants" element={
                <ErrorBoundary onError={handleError}>
                  <AnimalKingdomRestaurants updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/typhoon-lagoon" element={
                <ErrorBoundary onError={handleError}>
                  <TyphoonLagoon onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/blizzard-beach" element={
                <ErrorBoundary onError={handleError}>
                  <BlizzardBeach onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              {/* Nouveaux parcs */}
              <Route path="/alton-towers" element={
                <ErrorBoundary onError={handleError}>
                  <AltonTowers onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/universal-studios-singapore" element={
                <ErrorBoundary onError={handleError}>
                  <UniversalStudiosSingapore onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/volcano-bay" element={
                <ErrorBoundary onError={handleError}>
                  <VolcanoBay onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/legoland-deutschland" element={
                <ErrorBoundary onError={handleError}>
                  <LegolandDeutschland onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/tokyo-disneysea" element={
                <ErrorBoundary onError={handleError}>
                  <TokyoDisneySea onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/carowinds" element={
                <ErrorBoundary onError={handleError}>
                  <Carowinds onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/kennywood" element={
                <ErrorBoundary onError={handleError}>
                  <Kennywood onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/heide-park" element={
                <ErrorBoundary onError={handleError}>
                  <HeidePark onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/knoebels" element={
                <ErrorBoundary onError={handleError}>
                  <Knoebels onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/silver-dollar-city" element={
                <ErrorBoundary onError={handleError}>
                  <SilverDollarCity onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/la-ronde" element={
                <ErrorBoundary onError={handleError}>
                  <LaRonde onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/kings-island" element={
                <ErrorBoundary onError={handleError}>
                  <KingsIsland onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/hersheypark" element={
                <ErrorBoundary onError={handleError}>
                  <Hersheypark onError={handleError} updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              {GENERIC_THEMEPARKS_CONTENT_PARKS.map(({ parkSlug, path }) => {
                const parkConfig = PARKS_CONFIG[parkSlug];

                if (!parkConfig) {
                  return null;
                }

                return (
                  <React.Fragment key={`${parkSlug}-generic-content-routes`}>
                    <Route path={`${path}/shows`} element={
                      <ErrorBoundary onError={handleError}>
                        <GenericThemeparkEntityPage
                          parkSlug={parkSlug}
                          parkConfig={parkConfig}
                          contentType="shows"
                          updateLoadingState={updateLoadingState}
                        />
                      </ErrorBoundary>
                    } />
                    <Route path={`${path}/restaurants`} element={
                      <ErrorBoundary onError={handleError}>
                        <GenericThemeparkEntityPage
                          parkSlug={parkSlug}
                          parkConfig={parkConfig}
                          contentType="restaurants"
                          updateLoadingState={updateLoadingState}
                        />
                      </ErrorBoundary>
                    } />
                    <Route path={`${path}/map`} element={
                      <Navigate to={path} replace />
                    } />
                  </React.Fragment>
                );
              })}
              <Route path="/support-us" element={
                <ErrorBoundary onError={handleError}>
                  <SupportUs updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/contact" element={
                <ErrorBoundary onError={handleError}>
                  <ContactForm updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />
              <Route path="/parks" element={
                <ErrorBoundary onError={handleError}>
                  <ParksList updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />


              {/* Système de routage dynamique désactivé temporairement pour éviter les doublons de routes */}

              {/* Deanrell Soundbox */}
              <Route path="/deanrell-soundbox" element={
                <ErrorBoundary onError={handleError}>
                  <DeanrellSoundbox updateLoadingState={updateLoadingState} />
                </ErrorBoundary>
              } />

              {/* Page de test du Splashscreen V2 */}
              <Route path="/splash-test" element={
                <ErrorBoundary onError={handleError}>
                  <SplashScreenTest />
                </ErrorBoundary>
              } />

              <Route path="*" element={<NotFound updateLoadingState={updateLoadingState} />} />
            </Routes></Suspense>
          <Footer />
        </div>

        {showSignupInviteBanner && !showInstallBanner && (
          <div className="signup-invite-popup" role="dialog" aria-label={signupInviteBannerVariant === 'dispatchMax' ? t('signupBanner.dispatchMax.ariaLabel', 'Découvrir le jeu Dispatch Max sur Magic Park Times') : t('signupBanner.ariaLabel', 'Créer un compte Magic Park Times')}>
            <button
              className="signup-invite-close"
              aria-label={t('common.close', 'Fermer')}
              onClick={handleDismissSignupInviteBanner}
            >
              ×
            </button>
            <div className="signup-banner-content">
              <div className="signup-banner-visual" aria-hidden="true">
                <img src="/images/android-chrome-192x192.png" alt="" className="signup-banner-visual-icon" width="72" height="72" />
              </div>
              <div className="signup-banner-text">
                {signupInviteBannerVariant === 'dispatchMax' ? (
                  <span className="signup-banner-kicker">{t('signupBanner.dispatchMax.kicker', 'Nouveau mini-jeu')}</span>
                ) : (
                  <span className="signup-banner-kicker">{t('signupBanner.kicker', 'Compte gratuit')}</span>
                )}
                {signupInviteBannerVariant === 'dispatchMax' ? (
                  <span className="signup-banner-line signup-banner-title">{t('signupBanner.dispatchMax.line1', 'Dispatch Max est sorti pour tous les membres Magic Park Times')}</span>
                ) : (
                  <span className="signup-banner-line signup-banner-title">{t('signupBanner.line1', 'Inscrivez-vous gratuitement sur Magic Park Times')}</span>
                )}
                {signupInviteBannerVariant === 'dispatchMax' ? (
                  <span className="signup-banner-line">{t('signupBanner.dispatchMax.line2', 'Découvrez le jeu, son univers rétro et le classement mensuel')}</span>
                ) : (
                  <span className="signup-banner-line">{t('signupBanner.line2', 'Sauvegardez vos favoris et retrouvez vos préférences partout')}</span>
                )}
                {signupInviteBannerVariant === 'dispatchMax' ? (
                  <span className="signup-banner-line signup-banner-highlight">{t('signupBanner.dispatchMax.line3', 'Connectez-vous ensuite pour jouer immédiatement depuis votre espace membre')}</span>
                ) : (
                  <span className="signup-banner-line signup-banner-highlight">{t('signupBanner.line3', 'Accédez aussi à des fonctionnalités exclusives réservées aux membres')}</span>
                )}
              </div>
            </div>
            <div className="signup-banner-actions">
              {signupInviteBannerVariant === 'dispatchMax' ? (
                <button className="signup-invite-primary-btn" onClick={handleDispatchMaxInviteClick}>
                  {t('signupBanner.dispatchMax.cta', 'Découvrir le jeu')}
                </button>
              ) : (
                <button className="signup-invite-primary-btn" onClick={handleSignupInviteClick}>
                  {t('signupBanner.cta', "Créer mon compte")}
                </button>
              )}
              {signupInviteBannerVariant !== 'dispatchMax' && (
                <button className="signup-login-btn" onClick={handleLoginInviteClick}>
                  {t('signupBanner.login', 'Déjà inscrit ? Se connecter')}
                </button>
              )}
            </div>
          </div>
        )}

      </div>
      <BackToTop excludedPaths={['/about', '/my-pass', '/asterix-pass', '/support-us', '/dispatch-max']} />
      <GlobalNotifications />
    </WeatherProvider>
  );
};

// Hook pour sauvegarder la route actuelle (doit être utilisé DANS le Router)
function usePersistedRoute() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname + location.search;
    // Ne pas sauvegarder la page d'accueil pour éviter d'écraser une route utile
    if (path && path !== '/') {
      localStorage.setItem('lastRoute', path);
      localStorage.setItem('lastRouteTimestamp', Date.now().toString());
    }
  }, [location]);
}

// Composant qui gère la persistance et restauration des routes (doit être DANS le Router)
function RouteManager() {
  usePersistedRoute();
  useRestoreLastRoute();
  return null; // Ce composant ne rend rien, il gère juste les effets
}

// Hook pour restaurer la dernière route (doit être utilisé DANS le Router)
function useRestoreLastRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasRestored = React.useRef(false);

  useEffect(() => {
    // Éviter les restaurations multiples
    if (hasRestored.current) return;

    // Ne restaurer que si on est sur la page d'accueil
    if (location.pathname !== '/') {
      hasRestored.current = true;
      return;
    }

    const lastRoute = localStorage.getItem('lastRoute');
    if (!lastRoute || lastRoute === '/') {
      hasRestored.current = true;
      return;
    }

    // Détecter si l'app est en mode standalone (PWA installée)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true // iOS spécifique
      || document.referrer.includes('android-app://'); // Android

    // Détecter iOS PWA
    const isIOSPWA = isIOSDevice() && isStandalone;

    // Vérifier si c'est un retour sur l'app (pas une nouvelle visite intentionnelle)
    // On restaure si: mode standalone, iOS PWA, ou si on vient du même site
    // On restaure aussi si le referrer est vide ET qu'on a une route sauvegardée récemment
    const lastRouteTimestamp = localStorage.getItem('lastRouteTimestamp');
    const isRecentRoute = lastRouteTimestamp && (Date.now() - parseInt(lastRouteTimestamp, 10)) < 24 * 60 * 60 * 1000; // 24h

    const shouldRestore = isStandalone || isIOSPWA
      || (!document.referrer && isRecentRoute);

    if (shouldRestore) {
      hasRestored.current = true;
      // Utiliser navigate avec replace pour éviter d'ajouter à l'historique
      navigate(lastRoute, { replace: true });
    } else {
      hasRestored.current = true;
    }
  }, [location.pathname, navigate]);
}


function App() {
  const { i18n, t } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [waitTimeThresholds, setWaitTimeThresholds] = useState({});
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // 1. Vérifier d'abord le localStorage (préférence utilisateur sauvegardée)
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }

    // 2. Par défaut global : mode sombre
    return true;
  });
  const [isHighContrast, setIsHighContrast] = useState(() => {
    const saved = localStorage.getItem('highContrast');
    return saved ? saved === 'true' : false;
  });
  const [isLoadingVisible, setIsLoadingVisible] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [currentResort, setCurrentResort] = useState('dlp');
  const [showSnow, setShowSnow] = useState(() => {
    const savedSnowPreference = localStorage.getItem('showSnow');
    if (savedSnowPreference !== null) {
      return JSON.parse(savedSnowPreference);
    }
    return false;
  });

  // État pour les splashscreens
  const [showTeslaSplash, setShowTeslaSplash] = useState(() => {
    // Vérifier si c'est un navigateur Tesla et si le splash n'a pas déjà été affiché
    return isTesla() && !sessionStorage.getItem('teslaSplashShown');
  });

  const [showGeneralSplash, setShowGeneralSplash] = useState(() => {
    // Afficher le splash général pour tous les autres navigateurs (non-Tesla)
    return !isTesla() && !sessionStorage.getItem('generalSplashShown');
  });
  const [fireworksTime, setFireworksTime] = useState(null);

  // ============================================================================
  // 📡 Écoute des événements d'authentification pour réinitialiser l'UI
  // ============================================================================
  useEffect(() => {
    const handleSignedOut = () => {
      // Réinitialiser le thème : préférence sauvegardée si disponible, sinon mode sombre par défaut
      const savedTheme = localStorage.getItem('theme');
      setIsDarkMode(savedTheme ? savedTheme === 'dark' : true);
      setIsHighContrast(false);
      setShowSnow(false);
    };

    const handleSignedIn = () => {
      // Relire les préférences depuis localStorage (qui vient d'être mis à jour par AuthContext)
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        setIsDarkMode(savedTheme === 'dark');
      }
      const savedContrast = localStorage.getItem('highContrast');
      if (savedContrast) {
        setIsHighContrast(savedContrast === 'true');
      }
      const savedSnow = localStorage.getItem('showSnow');
      if (savedSnow !== null) {
        setShowSnow(JSON.parse(savedSnow));
      }
      // Langue
      const savedLang = localStorage.getItem('i18nextLng') || localStorage.getItem('language');
      if (savedLang) {
        i18n.changeLanguage(savedLang);
      }
    };

    const cleanupSignedOut = onAuthEvent(AUTH_EVENTS.SIGNED_OUT, handleSignedOut);
    const cleanupSignedIn = onAuthEvent(AUTH_EVENTS.SIGNED_IN, handleSignedIn);

    return () => {
      cleanupSignedOut();
      cleanupSignedIn();
    };
  }, [i18n]);

  // Bannière d'installation PWA personnalisée
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isAppStandalone, setIsAppStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showInstallOverlay, setShowInstallOverlay] = useState(false);
  const [installBrowser, setInstallBrowser] = useState('');

  const showIOSRefresh = isIOS && isAppStandalone;

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    const isOnMobile = typeof isMobile === 'function' ? isMobile() : false;
    setIsAppStandalone(isStandalone);
    setIsIOS(typeof isIOSDevice === 'function' ? isIOSDevice() : false);

    let visitCount = 0;
    try {
      visitCount = parseInt(localStorage.getItem('pwaVisitCount') || '0', 10);
    } catch { /* Ignore localStorage errors */ }
    visitCount = Number.isFinite(visitCount) ? visitCount + 1 : 1;
    try { localStorage.setItem('pwaVisitCount', String(visitCount)); } catch { /* Ignore localStorage errors */ }

    const dismissedUntil = parseInt(localStorage.getItem('pwaBannerDismissedUntil') || '0', 10);
    const installedFlag = localStorage.getItem('pwaInstalled') === 'true';

    const maybeShowBanner = () => {
      const now = Date.now();
      if (!isOnMobile || isStandalone || installedFlag || now < dismissedUntil || visitCount < 6) {
        setShowInstallBanner(false);
        return;
      }
      // Sur iOS, pas de beforeinstallprompt: on peut afficher des instructions
      if ((typeof isIOSDevice === 'function' && isIOSDevice())) {
        setShowInstallBanner(true);
      }
    };

    const onBIP = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
      if (isOnMobile && !isStandalone && !installedFlag && Date.now() >= dismissedUntil && visitCount >= 6) {
        setShowInstallBanner(true);
      }
    };

    const onInstalled = () => {
      try { localStorage.setItem('pwaInstalled', 'true'); } catch { /* Ignore localStorage errors */ }
      setShowInstallBanner(false);
      setDeferredInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);

    const mql = window.matchMedia('(display-mode: standalone)');
    const onDisplayModeChange = (evt) => {
      setIsAppStandalone(evt.matches);
      if (evt.matches) setShowInstallBanner(false);
    };
    if (mql && mql.addEventListener) mql.addEventListener('change', onDisplayModeChange);

    maybeShowBanner();

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
      if (mql && mql.removeEventListener) mql.removeEventListener('change', onDisplayModeChange);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredInstallPrompt) {
      openInstallOverlay();
      return;
    }
    deferredInstallPrompt.prompt();
    try {
      const choice = await deferredInstallPrompt.userChoice;
      if (choice && choice.outcome === 'accepted') {
        try { localStorage.setItem('pwaInstalled', 'true'); } catch { /* Ignore localStorage errors */ }
        setShowInstallBanner(false);
      }
    } catch { /* Ignore prompt errors */ }
    setDeferredInstallPrompt(null);
  };

  const openInstallOverlay = () => {
    try {
      const ua = navigator.userAgent || '';
      if (ua.indexOf('Chrome') > -1 && ua.indexOf('Safari') > -1) {
        setInstallBrowser('chrome');
      } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
        setInstallBrowser('safari');
      } else if (ua.indexOf('Firefox') > -1) {
        setInstallBrowser('firefox');
      } else if (ua.indexOf('Edge') > -1 || ua.indexOf('Edg') > -1) {
        setInstallBrowser('edge');
      } else {
        setInstallBrowser('other');
      }
    } catch {
      /* Fallback to 'other' if browser detection fails */
      setInstallBrowser('other');
    }
    setShowInstallOverlay(true);
  };

  const handleDismissBanner = (durationMs = 7 * 24 * 60 * 60 * 1000) => {
    try { localStorage.setItem('pwaBannerDismissedUntil', String(Date.now() + durationMs)); } catch { /* Ignore localStorage errors */ }
    setShowInstallBanner(false);
  };

  // Fonction pour déterminer si les fleurs printanières doivent être affichées
  const shouldShowSpringFlowers = () => {
    const currentDate = new Date();
    return currentDate.getMonth() === 2 && currentDate.getDate() === 21;
  };

  useEffect(() => {
    setCurrentLanguage(i18n.language);
    // Détecter le navigateur Tesla et changer la langue si nécessaire
    if (isTesla()) {
      i18n.changeLanguage('en');
      localStorage.setItem('language', 'en'); // Persister la préférence
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Gérer la fermeture du splashscreen Tesla (mémoïsé pour éviter les re-renders)
  const handleTeslaSplashComplete = useCallback(() => {
    setShowTeslaSplash(false);
    sessionStorage.setItem('teslaSplashShown', 'true');
  }, []);

  // Gérer la fermeture du splashscreen général (mémoïsé pour éviter les re-renders)
  const handleGeneralSplashComplete = useCallback(() => {
    setShowGeneralSplash(false);
    sessionStorage.setItem('generalSplashShown', 'true');
  }, []);

  useEffect(() => {
    // Appliquer la classe CSS selon le mode actuel avec requestAnimationFrame pour performances optimales
    requestAnimationFrame(() => {
      document.documentElement.classList.toggle('dark-mode', isDarkMode);
    });
    // Ne PAS sauvegarder automatiquement dans localStorage
    // La sauvegarde se fait uniquement lors du toggle manuel par l'utilisateur
  }, [isDarkMode]);

  // Appliquer le mode contraste élevé via un attribut sur <html>
  useEffect(() => {
    requestAnimationFrame(() => {
      if (isHighContrast) {
        document.documentElement.setAttribute('data-contrast', 'high');
      } else {
        document.documentElement.setAttribute('data-contrast', 'normal');
      }
    });
  }, [isHighContrast]);

  useEffect(() => {
    const styleId = 'luciole-fonts';
    const injectFonts = () => {
      if (document.getElementById(styleId)) return;
      const css = "@font-face{font-family:'Luciole';src:url('/assets/Luciole_webfonts/Luciole-Regular/Luciole-Regular.woff2') format('woff2'),url('/assets/Luciole_webfonts/Luciole-Regular/Luciole-Regular.woff') format('woff'),url('/assets/Luciole_webfonts/Luciole-Regular/Luciole-Regular.ttf') format('truetype');font-weight:400;font-style:normal;font-display:swap;}" +
        "@font-face{font-family:'Luciole';src:url('/assets/Luciole_webfonts/Luciole-Italic/Luciole-Italic.woff2') format('woff2'),url('/assets/Luciole_webfonts/Luciole-Italic/Luciole-Italic.woff') format('woff'),url('/assets/Luciole_webfonts/Luciole-Italic/Luciole-Italic.ttf') format('truetype');font-weight:400;font-style:italic;font-display:swap;}" +
        "@font-face{font-family:'Luciole';src:url('/assets/Luciole_webfonts/Luciole-Bold/Luciole-Bold.woff2') format('woff2'),url('/assets/Luciole_webfonts/Luciole-Bold/Luciole-Bold.woff') format('woff'),url('/assets/Luciole_webfonts/Luciole-Bold/Luciole-Bold.ttf') format('truetype');font-weight:700;font-style:normal;font-display:swap;}" +
        "@font-face{font-family:'Luciole';src:url('/assets/Luciole_webfonts/Luciole-BoldItalic/Luciole-BoldItalic.woff2') format('woff2'),url('/assets/Luciole_webfonts/Luciole-BoldItalic/Luciole-BoldItalic.woff') format('woff'),url('/assets/Luciole_webfonts/Luciole-BoldItalic/Luciole-BoldItalic.ttf') format('truetype');font-weight:700;font-style:italic;font-display:swap;}";
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = css;
      document.head.appendChild(style);
    };
    const removeFonts = () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
    if (isHighContrast) {
      injectFonts();
    } else {
      removeFonts();
    }
  }, [isHighContrast]);

  // Définir l'attribut lang sur <html> selon la langue courante
  useEffect(() => {
    const lang = (currentLanguage || 'en').split('-')[0];
    document.documentElement.setAttribute('lang', lang);
  }, [currentLanguage]);

  useEffect(() => {
    initLazyLoading();
    const handleLocationChange = () => {
      setCurrentResort('dlp');
      const currentPath = window.location.pathname;
      const previousPath = sessionStorage.getItem('previousPath') || '';
      const isWDWPark = path => {
        return path.includes('magic-kingdom') ||
          path.includes('epcot') ||
          path.includes('hollywood-studios') ||
          path.includes('animal-kingdom') ||
          path.includes('typhoon-lagoon') ||
          path.includes('blizzard-beach');
      };
      if (isWDWPark(currentPath) && isWDWPark(previousPath) && currentPath !== previousPath) {
        setIsLoading(false);
        setIsLoadingVisible(false);
        sessionStorage.setItem('wdwNavigationTimestamp', Date.now());
      }
      sessionStorage.setItem('previousPath', currentPath);
    };

    handleLocationChange();
    const onPopState = () => handleLocationChange();
    const origPushState = history.pushState;
    const origReplaceState = history.replaceState;
    let notifyTimerId = null;
    const notify = () => {
      if (notifyTimerId !== null) {
        clearTimeout(notifyTimerId);
      }
      notifyTimerId = setTimeout(() => {
        notifyTimerId = null;
        window.dispatchEvent(new Event('locationchange'));
      }, 0);
    };
    history.pushState = function (...args) {
      const ret = origPushState.apply(this, args);
      notify();
      return ret;
    };
    history.replaceState = function (...args) {
      const ret = origReplaceState.apply(this, args);
      notify();
      return ret;
    };
    window.addEventListener('popstate', onPopState);
    window.addEventListener('locationchange', handleLocationChange);
    return () => {
      if (notifyTimerId !== null) {
        clearTimeout(notifyTimerId);
      }
      history.pushState = origPushState;
      history.replaceState = origReplaceState;
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('locationchange', handleLocationChange);
    };
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };

  const toggleHighContrast = () => {
    setIsHighContrast(prev => {
      const next = !prev;
      localStorage.setItem('highContrast', String(next));
      return next;
    });
  };

  const setWaitTimeThreshold = (attractionId, threshold) => {
    setWaitTimeThresholds(prev => ({
      ...prev,
      [attractionId]: threshold
    }));
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
    setTimeout(() => setError(null), 5000);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    setIsLanguageMenuOpen(false);
  };

  const updateLoadingState = useCallback((loadingState) => {
    // Éviter les mises à jour inutiles (lecture via ref pour stabilité)
    if (loadingState === isLoadingRef.current) return;

    if (loadingState) {
      setIsLoading(true);
      setIsLoadingVisible(true);
    } else {
      setIsLoading(false);
      setTimeout(() => {
        setIsLoadingVisible(false);
      }, 500);
    }
  }, []);

  const toggleSnow = () => {
    setShowSnow(prev => {
      const newValue = !prev;
      localStorage.setItem('showSnow', JSON.stringify(newValue));
      return newValue;
    });
  };

  return (
    <HelmetProvider>
      <AuthProvider>
        <ModalProvider>
          <GraphProvider>
            <InfoButtonProvider>
              <WDWProvider>
                <LocalTimeProvider>
                  <LiquidGlassProvider>
                    <Router>
                      <RouteManager />
                      <MaintenanceChecker>
                        <div className={`App ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                          {/* Bannière batterie faible */}
                          <BatteryBanner />
                          {shouldShowSpringFlowers() && (
                            <Suspense fallback={null}>
                              {React.createElement(lazy(() => import('./components/SpringFlowers')))}
                            </Suspense>
                          )}

                          {/* Chargement de l'app en arrière-plan */}
                          <ChunkErrorBoundary>
                            <Suspense fallback={<div className="page-loading">Chargement...</div>}>
                              <LazyRoute>
                                <AppWithRouter
                                  isDarkMode={isDarkMode}
                                  toggleTheme={toggleTheme}
                                  isHighContrast={isHighContrast}
                                  toggleHighContrast={toggleHighContrast}
                                  showIOSRefresh={showIOSRefresh}
                                  handleError={handleError}
                                  setWaitTimeThreshold={setWaitTimeThreshold}
                                  waitTimeThresholds={waitTimeThresholds}
                                  isLoading={isLoading}
                                  isLoadingVisible={isLoadingVisible}
                                  updateLoadingState={updateLoadingState}
                                  currentLanguage={currentLanguage}
                                  isLanguageMenuOpen={isLanguageMenuOpen}
                                  setIsLanguageMenuOpen={setIsLanguageMenuOpen}
                                  changeLanguage={changeLanguage}
                                  t={t}
                                  showSnow={showSnow}
                                  toggleSnow={toggleSnow}
                                  showInstallBanner={showInstallBanner}
                                  fireworksTime={fireworksTime}
                                  setFireworksTime={setFireworksTime}
                                />
                              </LazyRoute>
                            </Suspense>
                          </ChunkErrorBoundary>
                          {/* Masquer BottomNavigation pendant le splashscreen */}
                          {!showTeslaSplash && !showGeneralSplash && (
                            <BottomNavigation
                              isDarkMode={isDarkMode}
                              toggleTheme={toggleTheme}
                              isHighContrast={isHighContrast}
                              toggleHighContrast={toggleHighContrast}
                              currentLanguage={currentLanguage}
                              isLanguageMenuOpen={isLanguageMenuOpen}
                              setIsLanguageMenuOpen={setIsLanguageMenuOpen}
                              changeLanguage={changeLanguage}
                              showSnow={showSnow}
                              toggleSnow={toggleSnow}
                              showIOSRefresh={showIOSRefresh}
                            />
                          )}
                          {showInstallBanner && !isAppStandalone && (
                            <div className="pwa-install-banner" role="dialog" aria-label="Installer Magic Park Times">
                              <div className="pwa-banner-text">
                                <span className="pwa-line">{t('installPwa.banner.l1', "Installez l'application")}</span>
                                <span className="pwa-line">{t('installPwa.banner.l2', 'Magic Park Times')}</span>
                                <span className="pwa-line">{t('installPwa.banner.l3', 'sur votre mobile ou tablette👌')}</span>
                              </div>
                              {isIOS ? (
                                <>
                                  <button className="pwa-install-btn" onClick={openInstallOverlay}>
                                    {t('installPwa.installNow', "Installer l'application")}
                                  </button>
                                  <button className="pwa-banner-close" aria-label={t('common.close', 'Fermer')} onClick={() => handleDismissBanner()}>×</button>
                                </>
                              ) : (
                                <>
                                  <button className="pwa-install-btn" onClick={handleInstallClick}>
                                    {t('installPwa.installNow', "Installer l'application")}
                                  </button>
                                  <button className="pwa-banner-close" aria-label={t('common.close', 'Fermer')} onClick={() => handleDismissBanner()}>×</button>
                                </>
                              )}
                            </div>
                          )}

                          {showInstallOverlay && (
                            <div className="pwa-install-overlay" role="dialog" aria-modal="true" aria-label={t('installPwa.title', 'Installez Magic Park Times')}>
                              <div className="pwa-install-card">
                                <div className="pwa-install-card-header">
                                  <span>{t('installPwa.title', 'Installez Magic Park Times')}</span>
                                  <button className="pwa-install-close" onClick={() => setShowInstallOverlay(false)}>×</button>
                                </div>
                                <div className="pwa-install-card-body">
                                  <p className="pwa-install-lead">{t('installPwa.description', 'Retrouvez Magic Park Times sur votre smartphone !')}</p>

                                  {isIOS ? (
                                    <div className="install-instructions">
                                      {installBrowser === 'safari' ? (
                                        <>
                                          <p><strong>{t('installPwa.ios.safari.title', 'Sur iPhone ou iPad avec Safari :')}</strong></p>
                                          <ol>
                                            <li>{t('installPwa.ios.safari.step1', 'Appuyez sur le bouton Partager')} <span role="img" aria-label="partager">⬆️</span> {t('installPwa.ios.safari.step1End', "en bas de l'écran")}</li>
                                            <li>{t('installPwa.ios.safari.step2', "Faites défiler et sélectionnez « Sur l'écran d'accueil »")}</li>
                                            <li>{t('installPwa.ios.safari.step3', "Validez en appuyant sur « Ajouter » en haut à droite")}</li>
                                          </ol>
                                        </>
                                      ) : (
                                        <>
                                          <p><strong>{t('installPwa.ios.other.title', 'Pour installer Magic Park Times sur iOS :')}</strong></p>
                                          <ol>
                                            <li>{t('installPwa.ios.other.step1', 'Ouvrez d\'abord cette page dans Safari')}</li>
                                            <li>{t('installPwa.ios.other.step2', 'Puis appuyez sur le bouton Partager')} <span role="img" aria-label="partager">⬆️</span> {t('installPwa.ios.other.step2End', "en bas de l'écran")}</li>
                                            <li>{t('installPwa.ios.other.step3', "Sélectionnez « Sur l'écran d'accueil »")}</li>
                                            <li>{t('installPwa.ios.other.step4', "Validez en appuyant sur « Ajouter »")}</li>
                                          </ol>
                                        </>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="install-instructions">
                                      {installBrowser === 'chrome' ? (
                                        <>
                                          <p><strong>{t('installPwa.android.chrome.title', 'Sur Android avec Chrome :')}</strong></p>
                                          <ol>
                                            <li>{t('installPwa.android.chrome.step1', "Appuyez sur l'icône ⋮ en haut à droite du navigateur")}</li>
                                            <li>{t('installPwa.android.chrome.step2', "Sélectionnez \"Ajouter à l'écran d'accueil\"")}</li>
                                            <li>{t('installPwa.android.chrome.step3', 'Validez en appuyant sur "Ajouter"')}</li>
                                          </ol>
                                        </>
                                      ) : installBrowser === 'firefox' ? (
                                        <>
                                          <p><strong>{t('installPwa.android.firefox.title', 'Sur Android avec Firefox :')}</strong></p>
                                          <ol>
                                            <li>{t('installPwa.android.firefox.step1', 'Appuyez sur les trois points ⋮ en haut à droite')}</li>
                                            <li>{t('installPwa.android.firefox.step2', 'Sélectionnez "Installer"')}</li>
                                          </ol>
                                        </>
                                      ) : (
                                        <>
                                          <p><strong>{t('installPwa.android.other.title', 'Sur Android :')}</strong></p>
                                          <ol>
                                            <li>{t('installPwa.android.other.step1', 'Ouvrez le menu de votre navigateur')}</li>
                                            <li>{t('installPwa.android.other.step2', "Recherchez l'option \"Ajouter à l'écran d'accueil\" ou \"Installer\"")}</li>
                                            <li>{t('installPwa.android.other.step3', "Suivez les instructions à l'écran")}</li>
                                          </ol>
                                        </>
                                      )}
                                    </div>
                                  )}

                                  {!isIOS && deferredInstallPrompt && (
                                    <button className="pwa-install-btn" onClick={handleInstallClick}>
                                      {t('installPwa.installNow', "Installer l'application")}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Splashscreens en overlay - permettent le chargement en arrière-plan */}
                          {showTeslaSplash && (
                            <TeslaSplashScreen onComplete={handleTeslaSplashComplete} />
                          )}

                          {showGeneralSplash && (
                            <GeneralSplashScreen
                              onComplete={handleGeneralSplashComplete}
                              isDarkMode={isDarkMode}
                            />
                          )}
                        </div>
                      </MaintenanceChecker>
                    </Router>
                  </LiquidGlassProvider>
                </LocalTimeProvider>
              </WDWProvider>
            </InfoButtonProvider>
          </GraphProvider>
        </ModalProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
